import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    GoogleMap,
    useJsApiLoader,
    OverlayView,
    InfoWindow,
    Polygon,
    Polyline as GPolyline,
} from "@react-google-maps/api";
import { Ambulance, Bus, Car, MapPin, AlertTriangle, X, ChevronDown, Building2Icon, Filter, Calendar, Clock, Hospital, ChevronUp, ArrowLeft } from "lucide-react";
import { getData } from "../../../Services/Services";
import { filterliveincidents, getlivedatadeploy, gisStateCoordinates, hospitalvehicledata, liveincident, getThemeURL } from "../../../Services/EndPoints";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import ambulance from '../../../Assets/Images/ambulanceto.png';
import facility from '../../../Assets/Images/campon.png';
import ActivePanelMapLegend from "./ActivePanelMapLegend";
import floodZone from '../../../Assets/Images/flood1.png';

const GOOGLE_MAPS_API_KEY = "AIzaSyDLzC-FhQmB8JS2H46OW46DNOEboBbrZVg";

const mapContainerStyle = { width: "100%", height: "100%" };

const disasterIconConfigs = {
    FLOOD: { color: "#3B82F6", icon: "💧", label: "Flood" },
    Flood: { color: "#3B82F6", icon: "💧", label: "Flood" },
    COLD_WAVE: { color: "#6B7280", icon: "❄️", label: "Cold Wave" },
    "Cold Wave": { color: "#6B7280", icon: "❄️", label: "Cold Wave" },
    EARTHQUAKE: { color: "#8B5CF6", icon: "🌍", label: "Earthquake" },
    Earthquake: { color: "#8B5CF6", icon: "🌍", label: "Earthquake" },
    FIRE: { color: "#DC2626", icon: "🔥", label: "Fire" },
    Fire: { color: "#DC2626", icon: "🔥", label: "Fire" },
    CYCLONE: { color: "#F59E0B", icon: "🌀", label: "Cyclone" },
    Cyclone: { color: "#F59E0B", icon: "🌀", label: "Cyclone" },
    "Heat Wave": { color: "#F97316", icon: "🔥", label: "Heat Wave" },
    HEAT_WAVE: { color: "#F97316", icon: "🔥", label: "Heat Wave" },
    Drought: { color: "#D97706", icon: "🌵", label: "Drought" },
    DROUGHT: { color: "#D97706", icon: "🌵", label: "Drought" },
    Landslide: { color: "#92400E", icon: "⛰️", label: "Landslide" },
    LANDSLIDE: { color: "#92400E", icon: "⛰️", label: "Landslide" },
    Tsunami: { color: "#0891B2", icon: "🌊", label: "Tsunami" },
    TSUNAMI: { color: "#0891B2", icon: "🌊", label: "Tsunami" },
    Tornado: { color: "#7C3AED", icon: "🌪️", label: "Tornado" },
    TORNADO: { color: "#7C3AED", icon: "🌪️", label: "Tornado" },
    Avalanche: { color: "#E0E7FF", icon: "🏔️", label: "Avalanche" },
    AVALANCHE: { color: "#E0E7FF", icon: "🏔️", label: "Avalanche" },
    Wildfire: { color: "#DC2626", icon: "🔥", label: "Wildfire" },
    WILDFIRE: { color: "#DC2626", icon: "🔥", label: "Wildfire" },
    DEFAULT: { color: "#6B7280", icon: "⚠️", label: "Incident" }
};

const placesIconConfigs = {
    'hospital': { color: '#2563eb', emoji: '🏥', label: 'Hospital', bgColor: '#eff6ff' },
    'pharmacy': { color: '#10b981', emoji: '💊', label: 'Pharmacy', bgColor: '#f0fdf4' },
    'fire_station': { color: '#dc2626', emoji: '🚒', label: 'Fire Station', bgColor: '#fef2f2' },
    'police': { color: '#3b82f6', emoji: '👮', label: 'Police Station', bgColor: '#eff6ff' },
    'gas_station': { color: '#059669', emoji: '⛽', label: 'Fuel Station', bgColor: '#ecfdf5' },
    'bank': { color: '#d97706', emoji: '🏦', label: 'Bank / ATM', bgColor: '#fffbeb' },
    'atm': { color: '#d97706', emoji: '🏧', label: 'Bank / ATM', bgColor: '#fffbeb' },
    'school': { color: '#f59e0b', emoji: '🏫', label: 'School (Shelter)', bgColor: '#fffbeb' },
    'lodging': { color: '#7c3aed', emoji: '🏨', label: 'Hotel (Refuge)', bgColor: '#f5f3ff' },
    'grocery_or_supermarket': { color: '#16a34a', emoji: '🛒', label: 'Grocery / Supply', bgColor: '#f0fdf4' },
    'supermarket': { color: '#16a34a', emoji: '🛒', label: 'Grocery / Supply', bgColor: '#f0fdf4' },
    'local_government_office': { color: '#8b5cf6', emoji: '🏛️', label: 'Govt. Office', bgColor: '#f5f3ff' },
    'helipad': { color: '#0ea5e9', emoji: '🚁', label: 'Helipad', bgColor: '#f0f9ff' },
    'DEFAULT': { color: '#6b7280', emoji: '📍', label: 'Place', bgColor: '#f9fafb' },
};

const NEARBY_PLACE_TYPES = [
    'hospital','fire_station','police','gas_station','pharmacy',
    'bank','school','lodging','grocery_or_supermarket','local_government_office','helipad',
];
const NEARBY_SEARCH_RADIUS = 10000;

const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
};

const isValidCoordinate = (lat, lng) => {
    const latitude = parseFloat(lat);
    const longitude = parseFloat(lng);
    return !isNaN(latitude) && !isNaN(longitude) &&
        latitude >= -90 && latitude <= 90 &&
        longitude >= -180 && longitude <= 180;
};

const filterGPSPoints = (points, minDistanceKm = 0.0001) => {
    if (points.length <= 2) return points;
    const filtered = [points[0]];
    for (let i = 1; i < points.length - 1; i++) {
        const lastPoint = filtered[filtered.length - 1];
        const currentPoint = points[i];
        const distance = calculateDistance(
            parseFloat(lastPoint.lat), parseFloat(lastPoint.lng),
            parseFloat(currentPoint.lat), parseFloat(currentPoint.lng)
        );
        if (distance >= minDistanceKm) filtered.push(currentPoint);
    }
    filtered.push(points[points.length - 1]);
    return filtered;
};

const extractIncidentType = (incidentName) => {
    if (!incidentName) return 'DEFAULT';
    const parts = incidentName.split('_');
    let possibleTypes = [parts[0]];
    if (parts.length >= 2) {
        possibleTypes.push(parts[0] + '_' + parts[1]);
        possibleTypes.push(parts[0] + ' ' + parts[1]);
    }
    const locationTimePattern = /_(?:(?!_)[A-Z]){2,}_\d+_\d+/;
    const baseName = incidentName.replace(locationTimePattern, '');
    if (baseName !== incidentName) possibleTypes.push(baseName);
    const typeMapping = {
        'Earthquake': 'Earthquake', 'EARTHQUAKE': 'Earthquake', 'earthquake': 'Earthquake',
        'Cold Wave': 'Cold Wave', 'Cold_Wave': 'Cold Wave', 'COLD_WAVE': 'Cold Wave', 'ColdWave': 'Cold Wave', 'coldwave': 'Cold Wave',
        'Flood': 'Flood', 'FLOOD': 'Flood', 'flood': 'Flood',
        'Fire': 'Fire', 'FIRE': 'Fire', 'fire': 'Fire',
        'Cyclone': 'Cyclone', 'CYCLONE': 'Cyclone', 'cyclone': 'Cyclone',
        'Heat Wave': 'Heat Wave', 'Heat_Wave': 'Heat Wave', 'HEAT_WAVE': 'Heat Wave', 'HeatWave': 'Heat Wave', 'heatwave': 'Heat Wave',
        'Drought': 'Drought', 'DROUGHT': 'Drought', 'drought': 'Drought',
        'Landslide': 'Landslide', 'LANDSLIDE': 'Landslide', 'landslide': 'Landslide',
        'Tsunami': 'Tsunami', 'TSUNAMI': 'Tsunami', 'tsunami': 'Tsunami',
        'Tornado': 'Tornado', 'TORNADO': 'Tornado', 'tornado': 'Tornado',
        'Avalanche': 'Avalanche', 'AVALANCHE': 'Avalanche', 'avalanche': 'Avalanche',
        'Wildfire': 'Wildfire', 'WILDFIRE': 'Wildfire', 'wildfire': 'Wildfire'
    };
    for (const possibleType of possibleTypes) {
        if (typeMapping[possibleType]) return typeMapping[possibleType];
    }
    return 'DEFAULT';
};

const reverseGeocode = async (lat, lon) => {
    try {
        if (!window.google?.maps?.Geocoder) return 'Geocoder not available';
        const geocoder = new window.google.maps.Geocoder();
        const response = await geocoder.geocode({ location: { lat: parseFloat(lat), lng: parseFloat(lon) } });
        if (response.results?.length > 0) {
            const filtered = response.results.filter(r => !r.types.includes('plus_code') && !r.formatted_address.includes('+'));
            const preferred = filtered.find(r => r.types.includes('street_address') || r.types.includes('route') || r.types.includes('premise') || r.types.includes('sublocality') || r.types.includes('neighborhood'));
            if (preferred) return preferred.formatted_address;
            if (filtered.length > 0) return filtered[0].formatted_address;
            const anyNonPlusCode = response.results.find(r => !r.formatted_address.includes('+'));
            if (anyNonPlusCode) return anyNonPlusCode.formatted_address;
        }
        return 'Address not available';
    } catch (error) { console.error('Geocoding error:', error); return 'Address lookup failed'; }
};

const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12; hours = hours ? hours : 12;
    hours = String(hours).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes} ${ampm}`;
};

const calculateETA = (currentLat, currentLng, destLat, destLng, speed) => {
    if (!currentLat || !currentLng || !destLat || !destLng || speed <= 0) return null;
    const distanceKm = calculateDistance(currentLat, currentLng, destLat, destLng);
    const timeMinutes = Math.round((distanceKm / speed) * 60);
    return { distance: distanceKm.toFixed(2), timeMinutes, formattedETA: formatETATime(timeMinutes) };
};

const formatETATime = (minutes) => {
    if (minutes < 1) return "Less than 1 min";
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (mins === 0) return `${hours} hr${hours > 1 ? 's' : ''}`;
    return `${hours} hr${hours > 1 ? 's' : ''} ${mins} min`;
};

const geoJsonToPaths = (geometry) => {
    if (!geometry) return [];
    const geom = typeof geometry === 'string' ? JSON.parse(geometry) : geometry;
    if (geom.type === 'Feature') return geoJsonToPaths(geom.geometry);
    if (geom.type === 'FeatureCollection') return geom.features.flatMap(f => geoJsonToPaths(f.geometry));
    if (geom.type === 'Polygon') return [geom.coordinates.map(ring => ring.map(c => ({ lat: c[1], lng: c[0] })))];
    if (geom.type === 'MultiPolygon') return geom.coordinates.map(poly => poly.map(ring => ring.map(c => ({ lat: c[1], lng: c[0] }))));
    return [];
};

const tryRoutingFallback = async (points) => {
    console.log('Trying routing fallback with', points.length, 'points...');
    try {
        if (points.length <= 25) {
            const coordinates = points.map(p => `${parseFloat(p.lng).toFixed(6)},${parseFloat(p.lat).toFixed(6)}`).join(';');
            const routeUrl = `https://router.project-osrm.org/route/v1/driving/${coordinates}?overview=full&geometries=geojson&continue_straight=false`;
            const response = await fetch(routeUrl);
            const data = await response.json();
            if (data.code === 'Ok' && data.routes && data.routes[0]) {
                const coords = data.routes[0].geometry.coordinates;
                return coords.map(coord => [coord[1], coord[0]]);
            }
        }
        const routedSegments = [];
        const step = Math.ceil(points.length / 30);
        for (let i = 0; i < points.length - 1; i += step) {
            const start = points[i];
            const end = points[Math.min(i + step, points.length - 1)];
            const routeUrl = `https://router.project-osrm.org/route/v1/driving/${parseFloat(start.lng).toFixed(6)},${parseFloat(start.lat).toFixed(6)};${parseFloat(end.lng).toFixed(6)},${parseFloat(end.lat).toFixed(6)}?overview=full&geometries=geojson`;
            const response = await fetch(routeUrl);
            const data = await response.json();
            if (data.code === 'Ok' && data.routes && data.routes[0]) {
                const coords = data.routes[0].geometry.coordinates;
                routedSegments.push(...(i === 0 ? coords : coords.slice(1)));
            } else {
                routedSegments.push([parseFloat(start.lng), parseFloat(start.lat)]);
                routedSegments.push([parseFloat(end.lng), parseFloat(end.lat)]);
            }
            await new Promise(resolve => setTimeout(resolve, 300));
        }
        if (routedSegments.length > 0) return routedSegments.map(coord => [coord[1], coord[0]]);
    } catch (error) { console.error('Routing fallback failed:', error); }
    return points.map(p => [parseFloat(p.lat), parseFloat(p.lng)]);
};

const snapPointsToRoad = async (points, maxRetries = 3) => {
    if (points.length < 2) return points.map(p => [p.lat, p.lng]);
    try {
        const validPoints = points.filter(point => {
            const lat = parseFloat(point.lat); const lng = parseFloat(point.lng);
            return !isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
        });
        if (validPoints.length < 2) return points.map(p => [p.lat || 0, p.lng || 0]);
        if (validPoints.length > 25) {
            const step = Math.ceil(validPoints.length / 20);
            const filteredForRouting = validPoints.filter((_, index) => index === 0 || index === validPoints.length - 1 || index % step === 0);
            return await tryRoutingFallback(filteredForRouting);
        }
        const filteredPoints = filterGPSPoints(validPoints, 0.001);
        for (let attempt = 0; attempt < maxRetries; attempt++) {
            try {
                const coordinates = filteredPoints.map(point => `${parseFloat(point.lng).toFixed(6)},${parseFloat(point.lat).toFixed(6)}`).join(';');
                const radiuses = filteredPoints.map(() => '100').join(';');
                const matchUrl = `https://router.project-osrm.org/match/v1/driving/${coordinates}?overview=full&geometries=geojson&radiuses=${radiuses}&steps=false&annotations=false&gaps=split&tidy=true`;
                if (matchUrl.length > 7000) return await tryRoutingFallback(filteredPoints);
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 25000);
                const response = await fetch(matchUrl, { method: 'GET', headers: { 'Accept': 'application/json', 'User-Agent': 'VehicleTracker/1.0' }, signal: controller.signal });
                clearTimeout(timeoutId);
                if (!response.ok) {
                    if (response.status === 400) return await tryRoutingFallback(filteredPoints);
                    throw new Error(`OSRM API returned ${response.status}`);
                }
                const data = await response.json();
                if (data.code === 'Ok' && data.matchings && data.matchings.length > 0) {
                    const allCoordinates = [];
                    data.matchings.forEach((matching, index) => {
                        if (matching.geometry && matching.geometry.coordinates) {
                            allCoordinates.push(...(index === 0 ? matching.geometry.coordinates : matching.geometry.coordinates.slice(1)));
                        }
                    });
                    if (allCoordinates.length > 0) return allCoordinates.map(coord => [coord[1], coord[0]]);
                } else if (data.code === 'NoMatch' && attempt === maxRetries - 1) {
                    return await tryRoutingFallback(filteredPoints);
                }
            } catch (error) {
                if (attempt < maxRetries - 1) { await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, attempt))); }
            }
        }
        return await tryRoutingFallback(filteredPoints);
    } catch (error) { console.error('Critical error:', error); return points.map(p => [parseFloat(p.lat) || 0, parseFloat(p.lng) || 0]); }
};

const IncidentMarkerIcon = ({ type, severity }) => {
    const icons = {
        FLOOD: { icon: "💧" }, Flood: { icon: "💧" }, COLD_WAVE: { icon: "❄️" }, "Cold Wave": { icon: "❄️" },
        EARTHQUAKE: { icon: "🌍" }, Earthquake: { icon: "🌍" }, FIRE: { icon: "🔥" }, Fire: { icon: "🔥" },
        CYCLONE: { icon: "🌀" }, Cyclone: { icon: "🌀" }, "Heat Wave": { icon: "🔥" }, HEAT_WAVE: { icon: "🔥" },
        Drought: { icon: "🌵" }, DROUGHT: { icon: "🌵" }, Landslide: { icon: "⛰️" }, LANDSLIDE: { icon: "⛰️" },
        Tsunami: { icon: "🏞️" }, TSUNAMI: { icon: "🏞️" }, Tornado: { icon: "🌪️" }, TORNADO: { icon: "🌪️" },
        Avalanche: { icon: "🏔️" }, AVALANCHE: { icon: "🏔️" }, Wildfire: { icon: "🔥" }, WILDFIRE: { icon: "🔥" },
        DEFAULT: { icon: "⚠️" }
    };
    const disaster = icons[type] || icons.DEFAULT;
    const isCritical = severity === "CRITICAL" || severity === "Critical" || severity === "High";
    return (
        <div style={{ position: "relative", width: "48px", height: "48px", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {isCritical && (<><div className="ripple-ring" /><div className="ripple-ring" /><div className="ripple-ring" /><div className="pulse-dot" /></>)}
            <div style={{ background: "white", width: "48px", height: "48px", borderRadius: "50%", border: "4px solid #DC2626", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 12px rgba(0,0,0,0.15)", position: "relative", zIndex: 2 }}>
                <div style={{ background: "#DC2626", width: "34px", height: "34px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: "18px" }}>{disaster.icon}</div>
            </div>
        </div>
    );
};

const AmbulanceMarkerIcon = ({ speed = 0 }) => {
    const speedColor = speed > 40 ? '#dc2626' : '#059669';
    return (
        <div style={{ background: 'rgba(197, 194, 194, 0.4)', width: "48px", height: "48px", borderRadius: "50%", border: "2px solid white", display: "flex", alignItems: "center", justifyContent: "center", position: "relative", overflow: "visible", fontSize: "30px" }}>
            🚑
            <div style={{ position: "absolute", top: "-10px", right: "-10px", backgroundColor: speedColor, color: "white", width: "28px", height: "28px", borderRadius: "50%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", border: "2px solid white", boxShadow: "0 2px 6px rgba(0,0,0,0.3)", zIndex: 10, lineHeight: "1" }}>
                <div style={{ fontSize: "10px", fontWeight: "bold" }}>{speed}</div>
                <div style={{ fontSize: "6px", marginTop: "-1px" }}>km/h</div>
            </div>
        </div>
    );
};

const VehicleMarkerIcon = ({ vehicleData }) => {
    const vehicleType = vehicleData.vehicleType?.toLowerCase() || vehicleData.vehicle_type?.toLowerCase() || '';
    const speed = parseFloat(vehicleData.speed) || 0;
    if (vehicleType.includes('ambulance') || vehicleType.includes('medical')) return <AmbulanceMarkerIcon speed={speed} />;
    const speedColor = speed > 40 ? '#dc2626' : '#059669';
    const iconColor = vehicleType.includes('bus') || vehicleType.includes('coach') ? '#3b82f6' : '#22c55e';
    const bgColor = vehicleType.includes('bus') || vehicleType.includes('coach') ? '#eff6ff' : '#f0fdf4';
    const IconComponent = vehicleType.includes('bus') || vehicleType.includes('coach') ? Bus : Car;
    return (
        <div style={{ backgroundColor: bgColor, width: '42px', height: '42px', borderRadius: '50%', border: '3px solid white', boxShadow: '0 3px 8px rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
            <IconComponent size={20} color={iconColor} strokeWidth={2.5} />
            <div style={{ position: 'absolute', top: '-10px', right: '-10px', backgroundColor: speedColor, color: 'white', width: '28px', height: '28px', borderRadius: '50%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: '2px solid white', boxShadow: '0 2px 6px rgba(0,0,0,0.3)', zIndex: 10, lineHeight: '1' }}>
                <div style={{ fontSize: '10px', fontWeight: 'bold' }}>{speed}</div>
                <div style={{ fontSize: '6px', marginTop: '-1px' }}>km/h</div>
            </div>
        </div>
    );
};

const FacilityMarkerIcon = () => (
    <div style={{ background: "white", width: "40px", height: "40px", borderRadius: "50%", border: "2px solid white", display: "flex", alignItems: "center", justifyContent: "center", position: "relative", overflow: "visible" }}>
        <img src={facility} alt="facility" style={{ width: "30px", height: "30px", objectFit: "contain" }} />
    </div>
);

const PlaceMarkerIcon = ({ placeType }) => {
    const config = placesIconConfigs[placeType] || placesIconConfigs.DEFAULT;
    return (
        <div style={{ background: config.bgColor, width: "42px", height: "42px", borderRadius: "50%", border: `3px solid ${config.color}`, boxShadow: `0 4px 12px ${config.color}40, 0 2px 4px rgba(0,0,0,0.2)`, display: "flex", alignItems: "center", justifyContent: "center", position: "relative", fontSize: "22px" }}>
            {config.emoji}
            <div style={{ position: "absolute", bottom: "-3px", right: "-3px", backgroundColor: config.color, width: "14px", height: "14px", borderRadius: "50%", border: "2px solid white", boxShadow: `0 2px 6px ${config.color}80` }} />
        </div>
    );
};

const VehiclePopup = ({ vehicleData, destinationCoords, incidentCoords }) => {
    const [address, setAddress] = useState('Loading address...');
    const [eta, setEta] = useState(null);
    const [distanceTraveled, setDistanceTraveled] = useState(null);
    useEffect(() => {
        const fetchAddress = async () => {
            if (vehicleData.latitude && vehicleData.longitude) {
                const addr = await reverseGeocode(parseFloat(vehicleData.latitude), parseFloat(vehicleData.longitude));
                setAddress(addr);
            } else { setAddress('Location not available'); }
        };
        fetchAddress();
        if (incidentCoords && vehicleData.latitude && vehicleData.longitude) {
            const d = calculateDistance(incidentCoords.lat, incidentCoords.lng, parseFloat(vehicleData.latitude), parseFloat(vehicleData.longitude));
            setDistanceTraveled(d.toFixed(2));
        }
        if (destinationCoords && vehicleData.latitude && vehicleData.longitude && vehicleData.speed) {
            const etaData = calculateETA(parseFloat(vehicleData.latitude), parseFloat(vehicleData.longitude), destinationCoords.lat, destinationCoords.lng, parseFloat(vehicleData.speed));
            setEta(etaData);
        }
    }, [vehicleData, destinationCoords, incidentCoords]);
    const calculateProgress = () => {
        if (!vehicleData.distance || !eta) return 0;
        const totalDistance = parseFloat(vehicleData.distance);
        const remainingDistance = parseFloat(eta.distance);
        const traveled = totalDistance - remainingDistance;
        return Math.min(Math.round((traveled / totalDistance) * 100), 100);
    };
    const progress = calculateProgress();
    return (
        <div style={{ minWidth: "300px", maxWidth: "340px", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px", paddingBottom: "8px", borderBottom: "1px solid #e5e7eb" }}>
                <div style={{ width: "42px", height: "42px", backgroundColor: "#fef2f2", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center" }}><Ambulance size={24} color="#dc2626" strokeWidth={2} /></div>
                <div style={{ flex: 1 }}>
                    <div style={{ fontSize: "16px", fontWeight: "700", color: "#1f2937", marginBottom: "1px", letterSpacing: "0.3px" }}>{vehicleData.vehicleNumber || 'N/A'}</div>
                    <div style={{ fontSize: "11px", fontWeight: "600", color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>{vehicleData.vehicleType || 'AMBULANCE'}</div>
                </div>
            </div>
            {vehicleData.fromLocation && (
                <div style={{ marginBottom: "8px" }}>
                    <div style={{ fontSize: "11px", fontWeight: "600", color: "#6b7280", marginBottom: "4px" }}>Incident Name:</div>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px", padding: "6px 10px", backgroundColor: "#fef2f2", borderRadius: "6px", border: "1px solid #fecaca" }}>
                        <MapPin size={14} color="#dc2626" strokeWidth={2.5} />
                        <span style={{ fontSize: "12px", color: "#1f2937", fontWeight: "500", lineHeight: "1.3" }}>{vehicleData.fromLocation}</span>
                    </div>
                </div>
            )}
            {vehicleData.toLocation && (
                <div style={{ marginBottom: "8px" }}>
                    <div style={{ fontSize: "11px", fontWeight: "600", color: "#6b7280", marginBottom: "4px" }}>Facility Name:</div>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px", padding: "6px 10px", backgroundColor: "#f0fdf4", borderRadius: "6px", border: "1px solid #bbf7d0" }}>
                        <Building2Icon size={14} color="#059669" strokeWidth={2.5} />
                        <span style={{ fontSize: "12px", color: "#1f2937", fontWeight: "500", lineHeight: "1.3" }}>{vehicleData.toLocation}</span>
                    </div>
                </div>
            )}
            <div style={{ display: "flex", gap: "12px", marginBottom: "10px" }}>
                {vehicleData.distance && (<div style={{ display: "flex", alignItems: "center", gap: "5px" }}><MapPin size={13} color="#6b7280" /><span style={{ fontSize: "11px", fontWeight: "600", color: "#6b7280" }}>Distance:</span><span style={{ fontSize: "12px", fontWeight: "700", color: "#1f2937" }}>{vehicleData.distance} km</span></div>)}
                <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    <div style={{ width: "16px", height: "16px", borderRadius: "50%", backgroundColor: vehicleData.speed > 40 ? "#fee2e2" : "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center" }}><div style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: vehicleData.speed > 40 ? "#dc2626" : "#059669" }} /></div>
                    <span style={{ fontSize: "11px", fontWeight: "600", color: "#6b7280" }}>Speed:</span>
                    <span style={{ fontSize: "12px", fontWeight: "700", color: vehicleData.speed > 40 ? "#dc2626" : "#059669" }}>{vehicleData.speed || 0} km/h</span>
                </div>
            </div>
            {eta && (
                <div style={{ padding: "10px", backgroundColor: "#eff6ff", borderRadius: "8px", border: "1px solid #bfdbfe", marginBottom: "10px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px" }}><Clock size={14} color="#2563eb" strokeWidth={2.5} /><span style={{ fontSize: "11px", fontWeight: "600", color: "#1e40af" }}>Estimation Time to Facility</span></div>
                    <div style={{ fontSize: "20px", fontWeight: "700", color: "#1e40af", marginBottom: "4px", lineHeight: "1.2" }}>{eta.formattedETA}</div>
                    <div style={{ fontSize: "11px", color: "#6b7280", marginBottom: "8px" }}>{eta.distance} km remaining</div>
                    <div style={{ width: "100%", height: "6px", backgroundColor: "#dbeafe", borderRadius: "3px", overflow: "hidden", position: "relative" }}>
                        <div style={{ width: `${progress}%`, height: "100%", backgroundColor: "#2563eb", transition: "width 0.3s ease", borderRadius: "3px" }} />
                    </div>
                    <div style={{ fontSize: "10px", color: "#6b7280", marginTop: "3px", textAlign: "right", fontWeight: "600" }}>{progress}%</div>
                    {vehicleData.speed === 0 && (<div style={{ marginTop: "6px", fontSize: "10px", color: "#dc2626", fontStyle: "italic", display: "flex", alignItems: "center", gap: "4px" }}><AlertTriangle size={11} /> Vehicle is stationary</div>)}
                </div>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: "5px", marginBottom: "8px", fontSize: "11px" }}>
                <Calendar size={13} color="#6b7280" />
                <span style={{ fontWeight: "600", color: "#6b7280" }}>Vehicle Tracking Date and Time:</span>
                <span style={{ color: "#1f2937", fontWeight: "500", fontSize: "11px" }}>{vehicleData.trackingTime ? formatDateTime(vehicleData.trackingTime) : 'N/A'}</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "10px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    <span style={{ fontSize: "11px", fontWeight: "600", color: "#6b7280" }}>Status:</span>
                    <span style={{ padding: "3px 10px", borderRadius: "5px", fontSize: "11px", fontWeight: "600", backgroundColor: vehicleData.eventType === 'stable' ? "#dbeafe" : "#fef3c7", color: vehicleData.eventType === 'stable' ? "#1e40af" : "#d97706" }}>{vehicleData.eventType || 'stable'}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    <div style={{ width: "7px", height: "7px", borderRadius: "50%", backgroundColor: "#10b981" }} />
                    <span style={{ fontSize: "11px", fontWeight: "600", color: "#10b981" }}>Active</span>
                </div>
            </div>
            <div style={{ paddingTop: "8px", borderTop: "1px solid #e5e7eb" }}>
                <div style={{ display: "flex", gap: "6px", alignItems: "flex-start" }}>
                    <MapPin size={13} style={{ marginTop: "2px", flexShrink: 0, color: "#6b7280" }} />
                    <div style={{ fontSize: "11px", color: "#6b7280", lineHeight: "1.4" }}>{address}</div>
                </div>
            </div>
        </div>
    );
};

const FacilityPopup = ({ facilityData }) => (
    <div className="text-sm" style={{ minWidth: "250px", maxWidth: "300px" }}>
        <div style={{ marginBottom: "12px" }}>
            <h3 style={{ fontSize: "16px", fontWeight: "600", marginBottom: "8px", color: "#059669" }}>Resource Facility</h3>
            <p style={{ margin: "6px 0" }}><strong>Resource Name:</strong> {facilityData.resourceName || 'N/A'}</p>
            <p style={{ margin: "6px 0" }}><strong>Facility Name:</strong> {facilityData.resourceFacility || 'N/A'}</p>
            <p style={{ margin: "6px 0" }}><strong>Agency:</strong> {facilityData.agencyName || 'N/A'}</p>
            <p style={{ margin: "6px 0" }}><strong>District:</strong> {facilityData.districtName || 'N/A'}</p>
            <p style={{ margin: "6px 0" }}><strong>Block:</strong> {facilityData.blockName || 'N/A'}</p>
        </div>
    </div>
);

const IncidentPopup = ({ incidentData }) => {
    const [address, setAddress] = useState('Loading address...');
    const [loadingAddress, setLoadingAddress] = useState(true);
    useEffect(() => {
        const fetchAddressFromCoordinates = async () => {
            try {
                if (incidentData.incidentLocation) {
                    const location = JSON.parse(incidentData.incidentLocation);
                    if (location.incidentcenter && Array.isArray(location.incidentcenter)) {
                        const [lng, lat] = location.incidentcenter;
                        const addr = await reverseGeocode(lat, lng);
                        setAddress(addr);
                    } else { setAddress('Coordinates not available'); }
                } else { setAddress('Location data not available'); }
            } catch (error) { setAddress('Address lookup failed'); } finally { setLoadingAddress(false); }
        };
        fetchAddressFromCoordinates();
    }, [incidentData]);
    return (
        <div className="text-sm" style={{ minWidth: "300px", maxWidth: "350px" }}>
            <h3 style={{ fontSize: "18px", fontWeight: "600", marginBottom: "12px", color: "#dc2626", borderBottom: "2px solid #fca5a5", paddingBottom: "8px" }}>Incident Details</h3>
            <div style={{ backgroundColor: "#fef2f2", padding: "14px", borderRadius: "6px" }}>
                <div style={{ marginBottom: "12px" }}><div style={{ fontSize: "12px", color: "#6b7280", fontWeight: "600", marginBottom: "4px" }}>Incident Name</div><div style={{ fontSize: "14px", fontWeight: "500", color: "#1f2937", lineHeight: "1.5" }}>{incidentData.incidentName || "Unnamed Incident"}</div></div>
                <div style={{ marginBottom: "12px" }}><div style={{ fontSize: "12px", color: "#6b7280", fontWeight: "600", marginBottom: "4px" }}>Incident Date & Time</div><div style={{ fontSize: "14px", fontWeight: "500", color: "#1f2937", lineHeight: "1.5" }}>{formatDateTime(incidentData.incidentTime)}</div></div>
                <div style={{ marginBottom: "12px" }}><div style={{ fontSize: "12px", color: "#6b7280", fontWeight: "600", marginBottom: "4px" }}>Incident Description</div><div style={{ fontSize: "14px", fontWeight: "500", color: "#1f2937", lineHeight: "1.5" }}>{incidentData.incidentDescription || "No description available"}</div></div>
                <div style={{ marginBottom: "12px" }}><div style={{ fontSize: "12px", color: "#6b7280", fontWeight: "600", marginBottom: "4px" }}>Incident Address</div><div style={{ fontSize: "14px", fontWeight: "500", color: "#1f2937", lineHeight: "1.5", fontStyle: loadingAddress ? "italic" : "normal" }}>{loadingAddress ? "Fetching address..." : address}</div></div>
            </div>
        </div>
    );
};

const PlacePopup = ({ placeData, incidentCenter }) => {
    const config = placesIconConfigs[placeData.layerType] || placesIconConfigs.DEFAULT;
    const distanceFromIncident = incidentCenter ? calculateDistance(incidentCenter.lat, incidentCenter.lng, parseFloat(placeData.latitude), parseFloat(placeData.longitude)).toFixed(2) : null;
    return (
        <div style={{ minWidth: "300px", maxWidth: "340px", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "14px", paddingBottom: "10px", borderBottom: `2px solid ${config.color}20` }}>
                <div style={{ width: "48px", height: "48px", borderRadius: "12px", backgroundColor: `${config.color}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "28px", border: `2px solid ${config.color}40` }}>{config.emoji}</div>
                <div style={{ flex: 1 }}>
                    <div style={{ fontSize: "16px", fontWeight: "700", color: "#1f2937", marginBottom: "4px", lineHeight: "1.3" }}>{placeData.name}</div>
                    <div style={{ fontSize: "11px", fontWeight: "600", color: config.color, textTransform: "uppercase", letterSpacing: "0.5px", padding: "3px 10px", backgroundColor: `${config.color}15`, borderRadius: "4px", display: "inline-block" }}>{config.label}</div>
                </div>
            </div>
            {placeData.vicinity && (<div style={{ backgroundColor: "#f9fafb", borderRadius: "8px", padding: "10px", marginBottom: "8px", border: "1px solid #e5e7eb" }}><div style={{ display: "flex", alignItems: "center", gap: "8px" }}><MapPin size={16} color={config.color} strokeWidth={2.5} /><div style={{ flex: 1 }}><div style={{ fontSize: "11px", color: "#6b7280", fontWeight: "600", marginBottom: "2px" }}>Address</div><div style={{ fontSize: "13px", color: "#1f2937", fontWeight: "600" }}>{placeData.vicinity}</div></div></div></div>)}
            {distanceFromIncident && (<div style={{ padding: "8px 10px", backgroundColor: "#eff6ff", borderRadius: "6px", border: "1px solid #bfdbfe", marginBottom: "8px", display: "flex", alignItems: "center", gap: "8px" }}><MapPin size={14} color="#2563eb" /><span style={{ fontSize: "12px", color: "#1e40af" }}><strong>Distance from incident:</strong> {distanceFromIncident} km</span></div>)}
            {placeData.rating && (<div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "8px", padding: "6px 10px", backgroundColor: "#fffbeb", borderRadius: "6px", border: "1px solid #fcd34d" }}><span style={{ fontSize: "14px" }}>⭐</span><span style={{ fontSize: "13px", fontWeight: "700", color: "#92400e" }}>{placeData.rating}</span>{placeData.userRatingsTotal && (<span style={{ fontSize: "11px", color: "#6b7280" }}>({placeData.userRatingsTotal} reviews)</span>)}</div>)}
            {placeData.openNow !== undefined && (<div style={{ display: "flex", alignItems: "center", gap: "6px", padding: "6px 10px" }}><div style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: placeData.openNow ? "#10b981" : "#ef4444" }} /><span style={{ fontSize: "12px", fontWeight: "600", color: placeData.openNow ? "#059669" : "#dc2626" }}>{placeData.openNow ? 'Open Now' : 'Closed'}</span></div>)}
            {distanceFromIncident && (<div style={{ marginTop: "8px", padding: "8px", backgroundColor: "#dbeafe", borderRadius: "6px", fontSize: "11px", color: "#1e40af", display: "flex", alignItems: "center", gap: "6px" }}><Clock size={12} /><span><strong>Estimated travel time:</strong> {formatETATime(Math.ceil(parseFloat(distanceFromIncident) / 40 * 60))} (@ 40 km/h)</span></div>)}
        </div>
    );
};

const HospitalPopup = ({ hospitalData, incidentName }) => (
    <div style={{ minWidth: "250px", maxWidth: "300px", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px", paddingBottom: "8px", borderBottom: "1px solid #e5e7eb" }}>
            <div style={{ width: "36px", height: "36px", backgroundColor: "#eff6ff", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center" }}><Building2Icon size={20} color="#2563eb" strokeWidth={2} /></div>
            <div style={{ flex: 1 }}><div style={{ fontSize: "16px", fontWeight: "600", color: "#1f2937", marginBottom: "2px" }}>{hospitalData.name || 'Hospital'}</div><div style={{ fontSize: "11px", fontWeight: "600", color: "#2563eb", textTransform: "uppercase", letterSpacing: "0.5px" }}>NEARBY HEALTHCARE FACILITY</div></div>
        </div>
        <div style={{ backgroundColor: "#f9fafb", borderRadius: "8px", padding: "12px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}><MapPin size={14} color="#6b7280" /><span style={{ fontSize: "12px", color: "#6b7280" }}><strong>Distance from incident:</strong></span><span style={{ fontSize: "14px", fontWeight: "600", color: "#2563eb" }}>{hospitalData.distanceKm ? `${hospitalData.distanceKm} km` : 'N/A'}</span></div>
            {incidentName && (<div style={{ marginTop: "8px", paddingTop: "8px", borderTop: "1px solid #e5e7eb", fontSize: "11px", color: "#6b7280" }}><strong>Associated Incident:</strong><br />{incidentName}</div>)}
            {hospitalData.distanceKm && (<div style={{ marginTop: "8px", padding: "8px", backgroundColor: "#dbeafe", borderRadius: "6px", fontSize: "11px", color: "#1e40af", display: "flex", alignItems: "center", gap: "6px" }}><Clock size={12} /><span><strong>Estimated travel time:</strong> {formatETATime(Math.ceil(hospitalData.distanceKm / 40 * 60))} (@ 40 km/h)</span></div>)}
        </div>
    </div>
);

const computeAllBoundsPoints = (selectedIncident, allIncidentsData, liveVehicleData, resourceData, roadSnappedRoutes) => {
    const points = [];
    const incidents = selectedIncident === "all" ? allIncidentsData : selectedIncident ? [selectedIncident] : [];
    incidents.forEach(incident => {
        try {
            if (incident.incidentLocation) {
                const loc = JSON.parse(incident.incidentLocation);
                if (loc.incidentcenter && Array.isArray(loc.incidentcenter)) {
                    const [lng, lat] = loc.incidentcenter;
                    if (isValidCoordinate(lat, lng)) points.push({ lat: parseFloat(lat), lng: parseFloat(lng) });
                }
            }
        } catch (e) { }
        const routeKey = incident.incidentId;
        if (roadSnappedRoutes && roadSnappedRoutes[routeKey]) {
            roadSnappedRoutes[routeKey].forEach(pt => { if (isValidCoordinate(pt[0], pt[1])) points.push({ lat: parseFloat(pt[0]), lng: parseFloat(pt[1]) }); });
        } else if (incident.RoutePoints && incident.RoutePoints.length > 0) {
            incident.RoutePoints.forEach(pt => { if (isValidCoordinate(pt.latitude, pt.longitude)) points.push({ lat: parseFloat(pt.latitude), lng: parseFloat(pt.longitude) }); });
        }
        incident.resources?.forEach(resource => {
            if (isValidCoordinate(resource.facilityLatitude, resource.facilityLongitude))
                points.push({ lat: parseFloat(resource.facilityLatitude), lng: parseFloat(resource.facilityLongitude) });
            if (!resourceData) {
                resource.vehicles?.forEach(vehicle => { if (isValidCoordinate(vehicle.latitude, vehicle.longitude)) points.push({ lat: parseFloat(vehicle.latitude), lng: parseFloat(vehicle.longitude) }); });
            }
        });
    });
    if (resourceData && liveVehicleData?.length > 0) {
        liveVehicleData.forEach(vehicle => { if (isValidCoordinate(vehicle.latitude, vehicle.longitude)) points.push({ lat: parseFloat(vehicle.latitude), lng: parseFloat(vehicle.longitude) }); });
    }
    return points;
};

const ActivePanelView = () => {
    const mapRef = useRef(null);
    const mapViewRef = useRef({ center: { lat: 34.550500, lng: 77.562200 }, zoom: 13 });
    const mapFitRef = useRef('pending');
    const hasInitializedRef = useRef(false);
    const userInteractedRef = useRef(false);
    const placesServiceRef = useRef(null);
    const mapContainerRef = useRef(null);
    const [isFullscreen, setIsFullscreen] = useState(false);

    useEffect(() => {
        const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
        document.addEventListener("fullscreenchange", onFsChange);
        return () => document.removeEventListener("fullscreenchange", onFsChange);
    }, []);

    const handleFullscreen = useCallback(() => {
        const el = mapContainerRef?.current;
        if (!el) return;
        if (!document.fullscreenElement) el.requestFullscreen?.();
        else document.exitFullscreen?.();
    }, []);
    const navigate = useNavigate();
    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: GOOGLE_MAPS_API_KEY,
        libraries: ["drawing", "geometry", "places", "visualization"],
    });

    const [incidentCenter, setIncidentCenter] = useState(null);
    const [incidentZoom, setIncidentZoom] = useState(10);
    const [stateGeoJSON, setStateGeoJSON] = useState(null);
    const [showResourcePanel, setShowResourcePanel] = useState(false);
    const [liveVehicleData, setLiveVehicleData] = useState([]);
    const [loadingVehicles, setLoadingVehicles] = useState(false);
    const [allIncidentsData, setAllIncidentsData] = useState([]);
    const [selectedIncident, setSelectedIncident] = useState(null);
    const [showIncidentDropdown, setShowIncidentDropdown] = useState(false);
    const [isLayerMenuOpen, setIsLayerMenuOpen] = useState(false);
    const [activeBaseLayer, setActiveBaseLayer] = useState('terrain');
    const [showAllLayers, setShowAllLayers] = useState(true);
    const [selectedLegendFilters, setSelectedLegendFilters] = useState([]);
    const [hospitalData, setHospitalData] = useState([]);
    const [isLegendExpanded, setIsLegendExpanded] = useState(true);
    const [showVehicles, setShowVehicles] = useState(true);
    const [showIncidents, setShowIncidents] = useState(true);
    const [showIncidentMarkers, setShowIncidentMarkers] = useState(true);
    const [showPolylines, setShowPolylines] = useState(true);
    const [showFilterDropdown, setShowFilterDropdown] = useState(false);
    const [roadSnappedRoutes, setRoadSnappedRoutes] = useState({});
    const [isSnappingRoutes, setIsSnappingRoutes] = useState(false);
    const [isFilterLoading, setIsFilterLoading] = useState(false);
    const [nearbyPlacesData, setNearbyPlacesData] = useState([]);
    const [showNearbyPlaces, setShowNearbyPlaces] = useState(true);
    const [loadingNearbyPlaces, setLoadingNearbyPlaces] = useState(false);
    const [facilityRoutes, setFacilityRoutes] = useState({});
    const [snappingFacilityId, setSnappingFacilityId] = useState(null);
    const [activeFacilityRouteId, setActiveFacilityRouteId] = useState(null);
    const [isFacilitiesExpanded, setIsFacilitiesExpanded] = useState(true);
    const [activeInfoWindow, setActiveInfoWindow] = useState(null);

    const ALWAYS_VISIBLE_DISASTERS = ['Earthquake', 'Flood', 'Avalanche'];
    const { state } = useLocation();
    const resourceData = state?.resourceData;

    const incidentData = resourceData ? {
        incident_name: resourceData.incident_name,
        resource_name: resourceData.resource_name,
        resource_facility_name: resourceData.resource_facility_name,
        resource_agency_name: resourceData.resource_agency_name,
        resource_region_name: resourceData.resource_region_name,
        available_quantity: resourceData.available_quantity,
        deployedResourceList: resourceData.deployedResourceList || []
    } : null;

    const mapTypeMap = { 'road': 'roadmap', 'satellite': 'satellite', 'terrain': 'terrain', 'hybrid': 'hybrid' };

    const getVehicleNumbers = () => {
        if (!resourceData?.deployedResourceList) return "";
        const vehicleNumbers = [];
        resourceData.deployedResourceList.forEach(deployment => {
            if (deployment.vehicles && Array.isArray(deployment.vehicles)) {
                deployment.vehicles.forEach(vehicle => { if (vehicle.vehicle_number) vehicleNumbers.push(vehicle.vehicle_number.trim()); });
            }
        });
        return vehicleNumbers.join(",");
    };

    useEffect(() => {
        if (!mapRef.current) return;
        if (!showAllLayers) {
            const incidents = selectedIncident === "all" ? allIncidentsData : selectedIncident ? [selectedIncident] : [];
            const alwaysVisiblePositions = [];
            incidents.forEach(incident => {
                const type = extractIncidentType(incident.incidentName);
                if (ALWAYS_VISIBLE_DISASTERS.includes(type) && incident.incidentLocation) {
                    try {
                        const loc = JSON.parse(incident.incidentLocation);
                        if (loc.incidentcenter && Array.isArray(loc.incidentcenter)) {
                            const [lng, lat] = loc.incidentcenter;
                            if (isValidCoordinate(lat, lng)) alwaysVisiblePositions.push({ lat: parseFloat(lat), lng: parseFloat(lng) });
                        }
                    } catch (e) { }
                }
            });
            if (alwaysVisiblePositions.length === 0) return;
            if (alwaysVisiblePositions.length === 1) { mapRef.current.panTo(alwaysVisiblePositions[0]); mapRef.current.setZoom(7); }
            else { const bounds = new window.google.maps.LatLngBounds(); alwaysVisiblePositions.forEach(p => bounds.extend(p)); mapRef.current.fitBounds(bounds, { top: 80, right: 80, bottom: 80, left: 80 }); }
        } else {
            const points = computeAllBoundsPoints(selectedIncident, allIncidentsData, liveVehicleData, resourceData, roadSnappedRoutes);
            if (points.length === 0) return;
            if (points.length === 1) { mapRef.current.panTo(points[0]); mapRef.current.setZoom(15); }
            else { const bounds = new window.google.maps.LatLngBounds(); points.forEach(p => bounds.extend(p)); mapRef.current.fitBounds(bounds, { top: 55, right: 55, bottom: 55, left: 55 }); }
        }
    }, [showAllLayers]); // eslint-disable-line react-hooks/exhaustive-deps

    const onMapLoad = useCallback((map) => {
        mapRef.current = map;
        try {
            if (window.google?.maps?.places?.PlacesService) {
                placesServiceRef.current = new window.google.maps.places.PlacesService(map);
            } else {
                const retryInterval = setInterval(() => {
                    if (window.google?.maps?.places?.PlacesService) { placesServiceRef.current = new window.google.maps.places.PlacesService(map); clearInterval(retryInterval); }
                }, 300);
                setTimeout(() => clearInterval(retryInterval), 10000);
            }
        } catch (error) { console.error('Failed to initialize PlacesService:', error); }
    }, []);

    const onMapIdle = useCallback(() => {
        if (!mapRef.current) return;
        const center = mapRef.current.getCenter();
        mapViewRef.current = { center: { lat: center.lat(), lng: center.lng() }, zoom: mapRef.current.getZoom() };
    }, []);

    const onMapDragStart = useCallback(() => { userInteractedRef.current = true; mapFitRef.current = 'user'; }, []);
    const onMapZoomChanged = useCallback(() => { if (!mapFitRef._programmatic) { userInteractedRef.current = true; mapFitRef.current = 'user'; } }, []);

    const incidentKey = selectedIncident === "all" ? "all" : selectedIncident?.incidentId ?? null;
    const vehicleCount = resourceData ? (liveVehicleData?.length || 0) : (allIncidentsData.reduce((sum, inc) => sum + inc.resources?.reduce((s, r) => s + (r.vehicles?.length || 0), 0), 0) || 0);
    const routeCount = Object.keys(roadSnappedRoutes || {}).length;
    const dataSignature = `${allIncidentsData.length}-${vehicleCount}-${routeCount}-${incidentKey}`;

    useEffect(() => {
        if (!mapRef.current) return;
        if (mapFitRef.current === 'user') return;
        if (allIncidentsData.length === 0 && !liveVehicleData?.length) return;
        const points = computeAllBoundsPoints(selectedIncident, allIncidentsData, liveVehicleData, resourceData, roadSnappedRoutes);
        if (points.length === 0) return;
        mapFitRef._programmatic = true;
        if (points.length === 1) { mapRef.current.panTo(points[0]); mapRef.current.setZoom(15); }
        else { const bounds = new window.google.maps.LatLngBounds(); points.forEach(p => bounds.extend(p)); mapRef.current.fitBounds(bounds, { top: 55, right: 55, bottom: 55, left: 55 }); }
        setTimeout(() => { mapFitRef._programmatic = false; mapFitRef.current = 'done'; }, 800);
    }, [dataSignature]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const style = document.createElement('style');
        style.innerHTML = `
            @keyframes pulse-red { 0% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.3); opacity: 0.5; } 100% { transform: scale(1); opacity: 1; } }
            @keyframes ripple-red { 0% { transform: scale(1); opacity: 0.8; } 100% { transform: scale(2); opacity: 0; } }
            .pulse-dot { position: absolute; width: 100%; height: 100%; border-radius: 50%; background: rgba(220, 38, 38, 0.6); animation: pulse-red 2s ease-in-out infinite; z-index: 0; }
            .ripple-ring { position: absolute; width: 100%; height: 100%; border-radius: 50%; border: 3px solid rgba(220, 38, 38, 0.8); animation: ripple-red 2s ease-out infinite; z-index: 0; }
            .ripple-ring:nth-child(2) { animation-delay: 0.5s; } .ripple-ring:nth-child(3) { animation-delay: 1s; }
            @keyframes spin { to { transform: rotate(360deg); } }
            .ap-stat-card:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(26,63,111,0.15) !important; }
            .ap-facility-card:hover { border-color: #93c5fd !important; box-shadow: 0 4px 12px rgba(59,130,246,0.15) !important; }
        `;
        document.head.appendChild(style);
        return () => { document.head.removeChild(style); };
    }, []);

    useEffect(() => {
        const performRoadSnapping = async () => {
            if (!showPolylines) return;
            const incidents = selectedIncident === "all" ? allIncidentsData : selectedIncident ? [selectedIncident] : [];
            if (incidents.length === 0) return;
            setIsSnappingRoutes(true);
            for (const incident of incidents) {
                if (incident.RoutePoints && incident.RoutePoints.length > 1 && !roadSnappedRoutes[incident.incidentId]) {
                    try {
                        const points = incident.RoutePoints.sort((a, b) => a.sequenceNo - b.sequenceNo).map(point => ({ lat: point.latitude, lng: point.longitude }));
                        const snappedRoute = await snapPointsToRoad(points);
                        setRoadSnappedRoutes(prev => ({ ...prev, [incident.incidentId]: snappedRoute }));
                        await new Promise(resolve => setTimeout(resolve, 1000));
                    } catch (error) {
                        console.error(`Error snapping route for incident ${incident.incidentId}:`, error);
                        const fallbackRoute = incident.RoutePoints.sort((a, b) => a.sequenceNo - b.sequenceNo).map(point => [point.latitude, point.longitude]);
                        setRoadSnappedRoutes(prev => ({ ...prev, [incident.incidentId]: fallbackRoute }));
                    }
                }
            }
            setIsSnappingRoutes(false);
        };
        performRoadSnapping();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allIncidentsData, selectedIncident, showPolylines]);

    const searchNearbyPlaces = useCallback((centerLat, centerLng) => {
        return new Promise((resolve) => {
            if (!placesServiceRef.current) { console.warn('PlacesService not initialized yet'); resolve([]); return; }
            const allResults = [];
            let completedSearches = 0;
            const totalSearches = NEARBY_PLACE_TYPES.length;
            NEARBY_PLACE_TYPES.forEach((placeType) => {
                const request = { location: new window.google.maps.LatLng(centerLat, centerLng), radius: NEARBY_SEARCH_RADIUS, type: placeType };
                placesServiceRef.current.nearbySearch(request, (results, status) => {
                    if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
                        const mapped = results.map((place) => ({ id: place.place_id, name: place.name, latitude: place.geometry.location.lat(), longitude: place.geometry.location.lng(), layerType: placeType, vicinity: place.vicinity || '', rating: place.rating || null, userRatingsTotal: place.user_ratings_total || null, openNow: place.opening_hours?.isOpen?.() ?? place.opening_hours?.open_now ?? undefined, businessStatus: place.business_status || '', placeId: place.place_id, icon: place.icon }));
                        allResults.push(...mapped);
                    }
                    completedSearches++;
                    if (completedSearches === totalSearches) resolve(allResults);
                });
            });
        });
    }, []);

    const fetchNearbyPlacesForIncidents = useCallback(async () => {
        if (!placesServiceRef.current) return;
        const incidents = selectedIncident === "all" ? allIncidentsData : selectedIncident && selectedIncident !== "all" ? [selectedIncident] : [];
        if (incidents.length === 0) { setNearbyPlacesData([]); return; }
        setLoadingNearbyPlaces(true);
        const allPlaces = [];
        const seenPlaceIds = new Set();
        for (const incident of incidents) {
            try {
                if (incident.incidentLocation) {
                    const location = JSON.parse(incident.incidentLocation);
                    if (location.incidentcenter && Array.isArray(location.incidentcenter)) {
                        const [lng, lat] = location.incidentcenter;
                        if (isValidCoordinate(lat, lng)) {
                            const places = await searchNearbyPlaces(parseFloat(lat), parseFloat(lng));
                            places.forEach((place) => { if (!seenPlaceIds.has(place.id)) { seenPlaceIds.add(place.id); allPlaces.push({ ...place, incidentId: incident.incidentId, incidentName: incident.incidentName }); } });
                        }
                    }
                }
            } catch (error) { console.error('Error fetching nearby places for incident:', incident.incidentId, error); }
        }
        setNearbyPlacesData(allPlaces);
        setLoadingNearbyPlaces(false);
    }, [selectedIncident, allIncidentsData, searchNearbyPlaces]);

    const snapIncidentToFacility = useCallback(async (incidentCoords, facilityCoords, facilityKey) => {
        if (!incidentCoords || !facilityCoords) return;
        if (facilityRoutes[facilityKey]) { setActiveFacilityRouteId(prev => prev === facilityKey ? null : facilityKey); return; }
        setSnappingFacilityId(facilityKey);
        try {
            const routeUrl = `https://router.project-osrm.org/route/v1/driving/${incidentCoords.lng},${incidentCoords.lat};${facilityCoords.lng},${facilityCoords.lat}?overview=full&geometries=geojson`;
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 20000);
            const response = await fetch(routeUrl, { signal: controller.signal });
            clearTimeout(timeoutId);
            const data = await response.json();
            if (data.code === 'Ok' && data.routes?.[0]) {
                const coords = data.routes[0].geometry.coordinates;
                const path = coords.map(c => ({ lat: c[1], lng: c[0] }));
                const distanceKm = (data.routes[0].distance / 1000).toFixed(2);
                const durationMin = Math.round(data.routes[0].duration / 60);
                setFacilityRoutes(prev => ({ ...prev, [facilityKey]: { path, distanceKm, durationMin } }));
                setActiveFacilityRouteId(facilityKey);
            } else {
                const path = [{ lat: incidentCoords.lat, lng: incidentCoords.lng }, { lat: facilityCoords.lat, lng: facilityCoords.lng }];
                setFacilityRoutes(prev => ({ ...prev, [facilityKey]: { path, distanceKm: null, durationMin: null, isFallback: true } }));
                setActiveFacilityRouteId(facilityKey);
                toast.warning("Road routing unavailable — showing straight-line path");
            }
        } catch (error) { console.error('Facility route snapping error:', error); toast.error("Failed to compute route to facility"); } finally { setSnappingFacilityId(null); }
    }, [facilityRoutes]);

    const fetchAllLiveVehicleData = async () => {
        setLoadingVehicles(true);
        try {
            const response = await getData(filterliveincidents);
            if (response.status === "success") {
                const processedData = response.data.map(incident => {
                    let routePoints = [];
                    if (incident.routePoints) { try { routePoints = typeof incident.routePoints === 'string' ? JSON.parse(incident.routePoints) : incident.routePoints; } catch (error) { console.error('Error parsing routePoints:', error); } }
                    return { ...incident, RoutePoints: routePoints };
                });
                setAllIncidentsData(processedData);
                setSelectedIncident(prev => {
                    if (prev === null) return "all";
                    if (prev === "all") return "all";
                    if (prev && prev.incidentId) { return processedData.find(i => i.incidentId === prev.incidentId) || prev; }
                    return prev;
                });
            }
        } catch (error) { console.error("Error fetching all live vehicle data:", error); toast.error("Failed to load live vehicle data"); } finally { setLoadingVehicles(false); }
    };

    const fetchResourceDataWithIncidents = async () => {
        setLoadingVehicles(true);
        try {
            const incidentsResponse = await getData(filterliveincidents);
            if (incidentsResponse.status === "success") {
                const processedData = incidentsResponse.data.map(incident => {
                    let routePoints = [];
                    if (incident.routePoints) { try { routePoints = typeof incident.routePoints === 'string' ? JSON.parse(incident.routePoints) : incident.routePoints; } catch (error) { } }
                    return { ...incident, RoutePoints: routePoints };
                });
                setAllIncidentsData(processedData);
                const matchingIncident = processedData.find(i => i.incidentName === resourceData.incident_name);
                if (matchingIncident) {
                    setSelectedIncident(matchingIncident);
                    if (!hasInitializedRef.current && matchingIncident.incidentLocation) {
                        try {
                            const location = JSON.parse(matchingIncident.incidentLocation);
                            if (location.incidentcenter && Array.isArray(location.incidentcenter)) {
                                const [lng, lat] = location.incidentcenter;
                                if (isValidCoordinate(lat, lng)) { mapViewRef.current = { center: { lat: parseFloat(lat), lng: parseFloat(lng) }, zoom: 13 }; hasInitializedRef.current = true; }
                            }
                        } catch (error) { }
                    }
                }
            }
            const vehicleNumbers = getVehicleNumbers();
            if (vehicleNumbers) {
                const vehicleResponse = await getData(`${getlivedatadeploy}${encodeURIComponent(vehicleNumbers)}`);
                if (vehicleResponse.status === "success") setLiveVehicleData(vehicleResponse.data);
            }
        } catch (error) { console.error("Error fetching resource data with incidents:", error); toast.error("Failed to load incident and vehicle data"); } finally { setLoadingVehicles(false); }
    };

    const mapStateCoordinates = async () => {
        try {
            const userProfile = await getData(getThemeURL);
            const stateId = userProfile && userProfile.length > 0 ? userProfile[0].state_id : null;
            const res = await getData(gisStateCoordinates + stateId);
            if (res && res.length > 0) {
                const stateData = res[0];
                const geoJsonData = { type: "Feature", properties: { name: stateData.state_name, state_id: stateData.state_id }, geometry: JSON.parse(stateData.coordinates) };
                setStateGeoJSON(geoJsonData);
            }
        } catch (error) { console.error("Error fetching state coordinates:", error); toast.error("Failed to load state coordinates"); }
    };

    useEffect(() => { mapStateCoordinates(); }, []);

    useEffect(() => {
        if (resourceData?.deployedResourceList) {
            fetchResourceDataWithIncidents();
            const interval = setInterval(fetchResourceDataWithIncidents, 5000);
            return () => clearInterval(interval);
        } else {
            fetchAllLiveVehicleData();
            const interval = setInterval(fetchAllLiveVehicleData, 5000);
            return () => clearInterval(interval);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resourceData]);

    useEffect(() => {
        if (placesServiceRef.current && allIncidentsData.length > 0) { fetchNearbyPlacesForIncidents(); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allIncidentsData, selectedIncident, placesServiceRef.current]);

    const handleIncidentSelect = (incident) => {
        setSelectedIncident(incident);
        setShowIncidentDropdown(false);
        setActiveInfoWindow(null);
        if (incident !== "all" && incident.incidentLocation) {
            try {
                const location = JSON.parse(incident.incidentLocation);
                if (location.incidentcenter) {
                    const [lng, lat] = location.incidentcenter;
                    if (isValidCoordinate(lat, lng)) { setIncidentCenter({ lat: parseFloat(lat), lng: parseFloat(lng) }); setIncidentZoom(12); }
                    else { setIncidentCenter(null); setIncidentZoom(6); }
                }
            } catch (error) { setIncidentCenter(null); setIncidentZoom(6); }
        } else { setIncidentCenter(null); setIncidentZoom(6); }
    };

    const getDeploymentStatus = () => {
        if (!resourceData?.deployedResourceList?.length) return { active: 0, released: 0, total: 0 };
        const active = resourceData.deployedResourceList.filter(d => d.is_deployed).reduce((sum, d) => sum + (d.deploy_quantity || 0), 0);
        const released = resourceData.deployedResourceList.filter(d => !d.is_deployed).reduce((sum, d) => sum + (d.deploy_quantity || 0), 0);
        return { active, released, total: resourceData.deployedResourceList.length };
    };

    const getTotalVehiclesCount = () => {
        if (selectedIncident === "all") return allIncidentsData.reduce((total, incident) => total + incident.resources.reduce((resTotal, resource) => resTotal + (resource.vehicles?.length || 0), 0), 0);
        if (!selectedIncident) return 0;
        return selectedIncident.resources.reduce((total, resource) => total + (resource.vehicles?.length || 0), 0);
    };

    const handleLegendFilterChange = useCallback((filtersOrUpdater) => {
        setIsFilterLoading(true);
        requestAnimationFrame(() => {
            setTimeout(() => { setSelectedLegendFilters(filtersOrUpdater); setIsFilterLoading(false); }, 50);
        });
    }, []);

    const shouldShowItem = (itemType, itemLabel) => {
        if (itemType === 'incident' && ALWAYS_VISIBLE_DISASTERS.includes(itemLabel)) return true;
        let filterLabel = itemLabel;
        if (itemType === 'facility') filterLabel = 'Facilities';
        else if (itemType === 'vehicle') filterLabel = 'Live Vehicles';
        else if (itemType === 'hospital') filterLabel = 'Nearby Hospitals';
        return selectedLegendFilters.some(filter => filter.type === itemType && filter.label === filterLabel);
    };

    const getIncidentCenterCoords = (incident) => {
        if (!incident || !incident.incidentLocation) return null;
        try {
            const location = JSON.parse(incident.incidentLocation);
            if (location.incidentcenter && Array.isArray(location.incidentcenter)) {
                const [lng, lat] = location.incidentcenter;
                if (isValidCoordinate(lat, lng)) return { lat: parseFloat(lat), lng: parseFloat(lng) };
            }
        } catch (e) { }
        return null;
    };

    const renderMapElements = () => {
        const elements = [];
        const polygonElements = [];
        const polylineElements = [];
        const openPopup = (key, position, content) => { setActiveInfoWindow({ key, position, content }); };

        const addIncidentOverlay = (incident, keyPrefix = '') => {
            if (!showIncidentMarkers) return;
            try {
                if (incident.incidentLocation) {
                    const location = JSON.parse(incident.incidentLocation);
                    if (location.incidentcenter && Array.isArray(location.incidentcenter)) {
                        const [lng, lat] = location.incidentcenter;
                        if (isValidCoordinate(lat, lng)) {
                            const position = { lat: parseFloat(lat), lng: parseFloat(lng) };
                            const incidentType = extractIncidentType(incident.incidentName);
                            const severity = incident.severityLevel || incident.severity || 'High';
                            const key = `incident-${keyPrefix}${incident.incidentId}`;
                            elements.push(<OverlayView key={key} position={position} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET} getPixelPositionOffset={() => ({ x: -24, y: -24 })}><div style={{ cursor: 'pointer' }} onClick={() => openPopup(key, position, <IncidentPopup incidentData={incident} />)}><IncidentMarkerIcon type={incidentType} severity={severity} /></div></OverlayView>);
                        }
                    }
                }
            } catch (error) { console.error("Error adding incident marker:", error); }
        };

        const addRoutePolyline = (incidentObj) => {
            if (!showPolylines || !incidentObj.RoutePoints || incidentObj.RoutePoints.length === 0) return;
            try {
                const routePoints = roadSnappedRoutes[incidentObj.incidentId] || incidentObj.RoutePoints.sort((a, b) => a.sequenceNo - b.sequenceNo).map(point => [point.latitude, point.longitude]);
                const isSnapped = !!roadSnappedRoutes[incidentObj.incidentId];
                const path = routePoints.map(pt => Array.isArray(pt) ? { lat: pt[0], lng: pt[1] } : { lat: pt.latitude || pt[0], lng: pt.longitude || pt[1] });
                polylineElements.push(<GPolyline key={`route-${incidentObj.incidentId}`} path={path} options={{ strokeColor: isSnapped ? "#ef4444" : "#f97316", strokeWeight: isSnapped ? 4 : 3, strokeOpacity: 0.7 }} onClick={() => openPopup(`route-${incidentObj.incidentId}`, path[Math.floor(path.length / 2)], (<div style={{ minWidth: "200px" }}><h4 style={{ fontSize: "14px", fontWeight: "600", marginBottom: "8px", color: isSnapped ? "#dc2626" : "#f97316" }}>{isSnapped ? "🛣️ Road-Aligned Route" : "📍 Direct Path"}</h4><p style={{ fontSize: "12px", color: "#6b7280", marginBottom: "4px" }}><strong>Incident:</strong> {incidentObj.incidentName}</p><p style={{ fontSize: "12px", color: "#6b7280", marginBottom: "4px" }}><strong>Route Points:</strong> {routePoints.length}</p><p style={{ fontSize: "12px", color: "#6b7280" }}><strong>Status:</strong> {isSnapped ? 'Snapped to Roads' : 'Processing...'}</p></div>))} />);
            } catch (error) { console.error('Error rendering route polyline:', error); }
        };

        const addVehicleOverlay = (vehicle, index, incidentObj, facilityDestination) => {
            if (!isValidCoordinate(vehicle.latitude, vehicle.longitude)) return;
            const position = { lat: parseFloat(vehicle.latitude), lng: parseFloat(vehicle.longitude) };
            const key = `vehicle-${vehicle.vehicleNumber || index}-${incidentObj?.incidentId || 'live'}`;
            let incidentOrigin = null;
            if (incidentObj?.incidentLocation) { try { const location = JSON.parse(incidentObj.incidentLocation); if (location.incidentcenter && Array.isArray(location.incidentcenter)) { const [lng, lat] = location.incidentcenter; incidentOrigin = { lat, lng }; } } catch (error) { } }
            const enhancedVehicle = { ...vehicle, fromLocation: incidentObj?.incidentName || vehicle.fromLocation || 'Incident Location', toLocation: incidentObj?.resources?.[0]?.resourceFacility || vehicle.toLocation || 'Facility Location', distance: vehicle.distance };
            elements.push(<OverlayView key={key} position={position} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET} getPixelPositionOffset={() => ({ x: -24, y: -24 })}><div style={{ cursor: 'pointer' }} onClick={() => openPopup(key, position, <VehiclePopup vehicleData={enhancedVehicle} destinationCoords={facilityDestination} incidentCoords={incidentOrigin} />)}><VehicleMarkerIcon vehicleData={vehicle} /></div></OverlayView>);
        };

        const addFacilityOverlay = (resource, keyPrefix = '') => {
            if (!isValidCoordinate(resource.facilityLatitude, resource.facilityLongitude)) return;
            const position = { lat: parseFloat(resource.facilityLatitude), lng: parseFloat(resource.facilityLongitude) };
            const facilityKey = `facility-${keyPrefix}${resource.resourceDeployId}`;
            let incidentOrigin = null;
            const incidents = selectedIncident === "all" ? allIncidentsData : selectedIncident ? [selectedIncident] : [];
            const parentIncident = incidents.find(inc => inc.resources?.some(r => r.resourceDeployId === resource.resourceDeployId)) || (selectedIncident !== "all" ? selectedIncident : null);
            if (parentIncident) incidentOrigin = getIncidentCenterCoords(parentIncident);
            const isSnapping = snappingFacilityId === facilityKey;
            const hasRoute = !!facilityRoutes[facilityKey];
            const isActiveRoute = activeFacilityRouteId === facilityKey;
            elements.push(<OverlayView key={facilityKey} position={position} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET} getPixelPositionOffset={() => ({ x: -20, y: -20 })}><div style={{ cursor: 'pointer', position: 'relative' }} onClick={() => { openPopup(facilityKey, position, <FacilityPopup facilityData={resource} />); if (incidentOrigin) { snapIncidentToFacility(incidentOrigin, position, facilityKey); } }}><FacilityMarkerIcon />{isSnapping && (<div style={{ position: 'absolute', top: '-8px', right: '-8px', width: '18px', height: '18px', borderRadius: '50%', border: '2px solid #e0e7ff', borderTopColor: '#3b82f6', animation: 'spin 0.8s linear infinite', backgroundColor: 'white' }} />)}{!isSnapping && hasRoute && (<div style={{ position: 'absolute', top: '-8px', right: '-8px', width: '18px', height: '18px', borderRadius: '50%', backgroundColor: isActiveRoute ? '#059669' : '#9ca3af', border: '2px solid white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', color: 'white', fontWeight: '700' }}>{isActiveRoute ? '✓' : '↺'}</div>)}</div></OverlayView>);
        };

        const addDistrictPolygon = (geoJsonStr, key) => {
            if (!geoJsonStr || !showIncidents) return;
            try { const geometry = typeof geoJsonStr === 'string' ? JSON.parse(geoJsonStr) : geoJsonStr; const pathSets = geoJsonToPaths(geometry); pathSets.forEach((paths, idx) => { polygonElements.push(<Polygon key={`${key}-${idx}`} paths={paths} options={{ strokeColor: "#e2730c", strokeWeight: 2, strokeOpacity: 1, fillColor: "#c4b5fd", fillOpacity: 0.15 }} />); }); } catch (error) { console.error("Error parsing district geometry:", error); }
        };

        const addBlockPolygon = (geoJsonStr, key) => {
            if (!geoJsonStr || !showIncidents) return;
            try { const geometry = typeof geoJsonStr === 'string' ? JSON.parse(geoJsonStr) : geoJsonStr; if (geometry && geometry.type && geometry.coordinates) { const pathSets = geoJsonToPaths(geometry); pathSets.forEach((paths, idx) => { polygonElements.push(<Polygon key={`${key}-${idx}`} paths={paths} options={{ strokeColor: "#eb6c6cff", strokeWeight: 1.5, fillColor: "#eb6c6cff", fillOpacity: 0.15 }} />); }); } } catch (error) { console.error("Error parsing block geometry:", error); }
        };

        const addPlaceOverlay = (place, index) => {
            const config = placesIconConfigs[place.layerType];
            if (!config || !isValidCoordinate(place.latitude, place.longitude)) return;
            const position = { lat: parseFloat(place.latitude), lng: parseFloat(place.longitude) };
            const placeKey = `place-${place.id || index}`;
            let incCenter = null;
            if (place.incidentId) { const inc = allIncidentsData.find(i => i.incidentId === place.incidentId); if (inc) incCenter = getIncidentCenterCoords(inc); }
            if (!incCenter && selectedIncident && selectedIncident !== "all") { incCenter = getIncidentCenterCoords(selectedIncident); }
            const isSnapping = snappingFacilityId === placeKey;
            const hasRoute = !!facilityRoutes[placeKey];
            const isActiveRoute = activeFacilityRouteId === placeKey;
            elements.push(<OverlayView key={placeKey} position={position} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET} getPixelPositionOffset={() => ({ x: -21, y: -21 })}><div style={{ cursor: 'pointer', zIndex: 1000, position: 'relative' }} onClick={() => { openPopup(placeKey, position, <PlacePopup placeData={place} incidentCenter={incCenter} />); if (incCenter) { snapIncidentToFacility(incCenter, position, placeKey); } }}><PlaceMarkerIcon placeType={place.layerType} />{isSnapping && (<div style={{ position: 'absolute', top: '-8px', right: '-8px', width: '18px', height: '18px', borderRadius: '50%', border: '2px solid #e0e7ff', borderTopColor: '#3b82f6', animation: 'spin 0.8s linear infinite', backgroundColor: 'white' }} />)}{!isSnapping && hasRoute && (<div style={{ position: 'absolute', top: '-8px', right: '-8px', width: '18px', height: '18px', borderRadius: '50%', backgroundColor: isActiveRoute ? '#059669' : '#9ca3af', border: '2px solid white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', color: 'white', fontWeight: '700' }}>{isActiveRoute ? '✓' : '↺'}</div>)}</div></OverlayView>);
        };

        if (resourceData) {
            if (selectedIncident && selectedIncident !== "all") {
                const incidentType = extractIncidentType(selectedIncident.incidentName);
                const config = disasterIconConfigs[incidentType] || disasterIconConfigs.DEFAULT;
                if (shouldShowItem('incident', config.label) && showIncidentMarkers) { addIncidentOverlay(selectedIncident); }
                if (shouldShowItem('polyline', 'Routes') && showPolylines) addRoutePolyline(selectedIncident);
                visibleResources.forEach((resource) => {
                    if (resource.facilityLatitude && resource.facilityLongitude && showIncidents && shouldShowItem('facility', 'Facilities')) { addFacilityOverlay(resource); }
                    addDistrictPolygon(resource.districtGeoJson, `district-resource-${resource.resourceDeployId}`);
                    addBlockPolygon(resource.blockGeometry, `block-resource-${resource.resourceDeployId}`);
                });
                if (liveVehicleData.length > 0 && showVehicles && shouldShowItem('vehicle', 'Live Vehicles')) {
                    liveVehicleData.forEach((vehicle, index) => {
                        let facilityDestination = null;
                        if (selectedIncident.resources?.length > 0) { const resource = selectedIncident.resources[0]; if (resource.facilityLatitude && resource.facilityLongitude) facilityDestination = { lat: parseFloat(resource.facilityLatitude), lng: parseFloat(resource.facilityLongitude) }; }
                        let vehicleDistance = vehicle.distance;
                        if (!vehicleDistance && selectedIncident.resources) { for (const resource of selectedIncident.resources) { if (resource.vehicles && Array.isArray(resource.vehicles)) { const matchingVehicle = resource.vehicles.find(v => v.vehicleNumber === vehicle.vehicleNumber); if (matchingVehicle?.distance) { vehicleDistance = matchingVehicle.distance; break; } } } }
                        addVehicleOverlay({ ...vehicle, distance: vehicleDistance }, index, selectedIncident, facilityDestination);
                    });
                }
                if (showNearbyPlaces && nearbyPlacesData.length > 0) { nearbyPlacesData.forEach((place, index) => { const placeConfig = placesIconConfigs[place.layerType]; if (placeConfig && shouldShowItem('nearby_place', placeConfig.label)) { addPlaceOverlay(place, index); } }); }
            }
        } else {
            const processIncident = (incident) => {
                const incidentType = extractIncidentType(incident.incidentName);
                const config = disasterIconConfigs[incidentType] || disasterIconConfigs.DEFAULT;
                if (shouldShowItem('incident', config.label) && showIncidentMarkers) addIncidentOverlay(incident);
                if (shouldShowItem('polyline', 'Routes') && showPolylines) addRoutePolyline(incident);
                incident.resources.forEach((resource) => {
                    if (resource.facilityLatitude && resource.facilityLongitude && showIncidents && shouldShowItem('facility', 'Facilities')) { addFacilityOverlay(resource, `${incident.incidentId}-`); }
                    addDistrictPolygon(resource.districtGeoJson, `district-${incident.incidentId}-${resource.resourceDeployId}`);
                    addBlockPolygon(resource.blockGeometry, `block-${incident.incidentId}-${resource.resourceDeployId}`);
                    if (showVehicles && shouldShowItem('vehicle', 'Live Vehicles')) { resource.vehicles?.forEach((vehicle, vIndex) => { let vehicleDestination = null; if (resource.facilityLatitude && resource.facilityLongitude) vehicleDestination = { lat: parseFloat(resource.facilityLatitude), lng: parseFloat(resource.facilityLongitude) }; addVehicleOverlay(vehicle, vIndex, incident, vehicleDestination); }); }
                });
            };
            if (selectedIncident === "all") { allIncidentsData.forEach(processIncident); }
            else if (selectedIncident) { processIncident(selectedIncident); }
        }

        if (!resourceData && showNearbyPlaces && nearbyPlacesData.length > 0) { nearbyPlacesData.forEach((place, index) => { const placeConfig = placesIconConfigs[place.layerType]; if (placeConfig && shouldShowItem('nearby_place', placeConfig.label)) { addPlaceOverlay(place, index); } }); }

        return { elements, polygonElements, polylineElements };
    };

    const deploymentStatus = getDeploymentStatus();

    // When navigated from DeploymentList, only show the specific resource(s) that were deployed.
    // Match by resourceDeployId against the deployedResourceList ids passed via route state.
    const visibleResources = (() => {
        if (!resourceData || !selectedIncident || selectedIncident === "all") return selectedIncident?.resources || [];
        const deployedIds = new Set((resourceData.deployedResourceList || []).map(d => d.id));
        return selectedIncident.resources.filter(r => deployedIds.has(r.resourceDeployId));
    })();

    if (loadError) return <div>Error loading maps</div>;
    if (!isLoaded) return <div>Loading maps…</div>;

    // ── Style tokens (GIS palette) ────────────────────────────────────────────
    const HEADER_BG = "linear-gradient(120deg, #2E6DA4 0%, #3B82C4 100%)";
    const SECTION_HEADER_BG = "linear-gradient(135deg, #1a3f6f 0%, #245a8d 100%)";
    const PANEL_BG = "#f4f7fb";
    const CARD_BG = "#ffffff";
    const CARD_BORDER = "1px solid rgba(30,58,95,0.1)";
    const ACCENT = "#2a7de1";
    const ACCENT_LIGHT = "rgba(42,125,225,0.08)";
    const TEXT_PRIMARY = "#1a2a3a";
    const TEXT_SECONDARY = "#5a6a7a";
    const TEXT_MUTED = "#8899aa";
    const INPUT_BORDER = "#d0dce8";
    const DIVIDER = "rgba(30,58,95,0.08)";
    const SHADOW = "0 4px 24px rgba(30,58,95,0.08)";

    
// Only show Live Tracking card when at least one resource has facilityType === "Transportation"
const hasTransportationResources = (() => {
    if (resourceData) {
        if (selectedIncident && selectedIncident !== "all") {
            return selectedIncident.resources?.some(r => r.facilityType === "Transportation") ?? false;
        }
        return false;
    }
    // non-resourceData path
    const incidents =
        selectedIncident === "all" ? allIncidentsData
        : selectedIncident        ? [selectedIncident]
        : [];
    return incidents.some(inc =>
        inc.resources?.some(r => r.facilityType === "Transportation")
    );
})();

    // Extracted so the exact same panel (with its expand/collapse state) can
    // also be rendered as an overlay inside the fullscreen map container —
    // requestFullscreen() only shows mapContainerRef's own subtree, so
    // without this the panel would vanish when the map goes fullscreen.
    const facilityDetailsPanel = (selectedIncident && selectedIncident !== "all" && visibleResources.length > 0) ? (
                        <div style={{ margin: "0 20px 12px", borderRadius: "10px", overflow: "hidden", border: `1.5px solid ${INPUT_BORDER}`, boxShadow: "0 1px 6px rgba(30,58,95,0.06)" }}>

                            {/* Toggle header — navy gradient like GIS section headers */}
                            <div
                                onClick={() => setIsFacilitiesExpanded(!isFacilitiesExpanded)}
                                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 16px", background: "linear-gradient(135deg, #1a3f6f 0%, #245a8d 100%)", cursor: "pointer", userSelect: "none", transition: "filter 0.15s" }}
                                onMouseEnter={e => e.currentTarget.style.filter = "brightness(1.1)"}
                                onMouseLeave={e => e.currentTarget.style.filter = "brightness(1)"}
                            >
                                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                    <div style={{ width: "24px", height: "24px", borderRadius: "6px", background: "rgba(255,255,255,0.18)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                        <Building2Icon size={13} color="white" strokeWidth={2.5} />
                                    </div>
                                    <span style={{ fontSize: "11.5px", fontWeight: "700", color: "white", letterSpacing: "0.04em", textTransform: "uppercase" }}>Resource Facility Details</span>
                                    <span style={{ fontSize: "10px", fontWeight: "700", background: "rgba(255,255,255,0.2)", color: "rgba(255,255,255,0.95)", padding: "1px 8px", borderRadius: "20px", border: "1px solid rgba(255,255,255,0.25)" }}>
                                        {visibleResources.length}
                                    </span>
                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                    {!isFacilitiesExpanded && visibleResources.length === 1 && (() => {
                                        const r = visibleResources[0];
                                        return (
                                            <div style={{ display: "flex", gap: "20px", fontSize: "11px" }}>
                                                {[{ label: "Resource", value: r.resourceName }, { label: "Agency", value: r.agencyName }, { label: "Facility", value: r.resourceFacility }].map(item => item.value && (
                                                    <span key={item.label} style={{ color: "rgba(255,255,255,0.7)" }}>
                                                        <span style={{ color: "rgba(255,255,255,0.45)", marginRight: "3px" }}>{item.label}:</span>
                                                        <span style={{ color: "#93c5fd", fontWeight: "600" }}>{item.value}</span>
                                                    </span>
                                                ))}
                                            </div>
                                        );
                                    })()}
                                    <div style={{ width: "22px", height: "22px", borderRadius: "50%", background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.25)", display: "flex", alignItems: "center", justifyContent: "center", transition: "transform 0.25s", transform: isFacilitiesExpanded ? "rotate(0deg)" : "rotate(180deg)" }}>
                                        <ChevronUp size={12} color="rgba(255,255,255,0.9)" />
                                    </div>
                                </div>
                            </div>

                            {/* Expanded body — clean white */}
                            {isFacilitiesExpanded && (
                                <div style={{ background: "white", overflowY: "auto", maxHeight: "260px" }}>
                                    {visibleResources.length === 1 ? (() => {
                                        const r = visibleResources[0];
                                        const FIELDS = [
                                            { label: "Resource Name", value: r.resourceName,     icon: "📦", dotColor: "#3b82f6" },
                                            { label: "Agency Name",   value: r.agencyName,       icon: "🏛️", dotColor: "#10b981" },
                                            { label: "Facility Name", value: r.resourceFacility,  icon: "🏢", dotColor: "#8b5cf6" },
                                            { label: "Deployed Quantity", value: r.deployQuantity,  icon: "🚀", dotColor: "#8b5cf6" },
                                            { label: "Available Quantity", value: r.availableQuantity,  icon: "📦", dotColor: "#8b5cf6" },
                                        ];
                                        return (
                                            <div style={{ display: "flex", alignItems: "stretch" }}>
                                                {FIELDS.map((field, idx) => (
                                                    <div key={field.label} style={{ flex: 1, minWidth: 0, padding: "11px 18px", borderRight: idx < FIELDS.length - 1 ? `1px solid ${DIVIDER}` : "none", display: "flex", flexDirection: "column", gap: "4px", position: "relative", overflow: "hidden" }}>
                                                        <div style={{ position: "absolute", top: 0, left: "18px", right: "18px", height: "2px", borderRadius: "0 0 4px 4px", background: field.dotColor, opacity: 0.6 }} />
                                                        <div style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "3px" }}>
                                                            <div style={{ width: "20px", height: "20px", borderRadius: "5px", background: `${field.dotColor}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "11px", flexShrink: 0 }}>{field.icon}</div>
                                                            <span style={{ fontSize: "9px", fontWeight: "700", color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.07em" }}>{field.label}</span>
                                                        </div>
                                                        <div style={{ fontSize: "13px", fontWeight: "700", color: field.dotColor, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", paddingLeft: "26px" }} title={field.value || "N/A"}>{field.value || "N/A"}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        );
                                    })() : (
                                        /* ── Multi-resource: vertical stack, each card full-width with 3 fields horizontal ── */
                                        <div style={{ display: "flex", flexDirection: "column" }}>
                                            {visibleResources.map((resource, index) => {
                                                const CARD_FIELDS = [
                                                    { label: "Resource Name", value: resource.resourceName,     icon: "📦", color: "#3b82f6" },
                                                    { label: "Agency Name",   value: resource.agencyName,       icon: "🏛️", color: "#10b981" },
                                                    { label: "Facility Name", value: resource.resourceFacility,  icon: "🏢", color: "#8b5cf6" },
                                                    { label: "Deployed Quantity", value: resource.deployQuantity,  icon: "🚀", color: "#8b5cf6" },
                                                    { label: "Available Quantity", value: resource.availableQuantity,  icon: "📦", color: "#8b5cf6" },
                                                ];
                                                const CARD_COLORS = ["#3b82f6", "#10b981", "#8b5cf6", "#f59e0b", "#ef4444"];
                                                const cardAccent = CARD_COLORS[index % CARD_COLORS.length];
                                                return (
                                                    <div key={index} className="ap-facility-card" style={{ width: "100%", borderTop: index > 0 ? `1px solid ${DIVIDER}` : "none", backgroundColor: "white", position: "relative", overflow: "hidden", transition: "background 0.15s" }}
                                                        onMouseEnter={e => e.currentTarget.style.backgroundColor = "#fafbff"}
                                                        onMouseLeave={e => e.currentTarget.style.backgroundColor = "white"}>
                                                        {/* Left accent bar */}
                                                        <div style={{ position: "absolute", top: 0, left: 0, width: "3px", height: "100%", background: `linear-gradient(180deg, ${cardAccent}, ${cardAccent}66)` }} />
                                                        {/* Index badge */}
                                                        <div style={{ position: "absolute", top: "50%", left: "10px", transform: "translateY(-50%)", width: "20px", height: "20px", borderRadius: "50%", background: `${cardAccent}15`, border: `1.5px solid ${cardAccent}35`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", fontWeight: "800", color: cardAccent }}>{index + 1}</div>
                                                        {/* 3 fields horizontal — same as single-resource layout */}
                                                        <div style={{ display: "flex", alignItems: "stretch", paddingLeft: "36px" }}>
                                                            {CARD_FIELDS.map((field, idx) => (
                                                                <div key={field.label} style={{ flex: 1, minWidth: 0, padding: "10px 16px", borderRight: idx < CARD_FIELDS.length - 1 ? `1px solid ${DIVIDER}` : "none", display: "flex", flexDirection: "column", gap: "4px", position: "relative", overflow: "hidden" }}>
                                                                    <div style={{ position: "absolute", top: 0, left: "16px", right: "16px", height: "2px", borderRadius: "0 0 4px 4px", background: field.color, opacity: 0.5 }} />
                                                                    <div style={{ display: "flex", alignItems: "center", gap: "5px", marginTop: "3px" }}>
                                                                        <div style={{ width: "18px", height: "18px", borderRadius: "4px", background: `${field.color}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", flexShrink: 0 }}>{field.icon}</div>
                                                                        <span style={{ fontSize: "9px", fontWeight: "700", color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.07em" }}>{field.label}</span>
                                                                    </div>
                                                                    <div style={{ fontSize: "12px", fontWeight: "700", color: field.color, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", paddingLeft: "23px" }} title={field.value || "N/A"}>{field.value || "N/A"}</div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
    ) : null;

    return (
        <div style={{ height: "100vh", width: "100%", display: "flex", flexDirection: "column", backgroundColor: PANEL_BG }}>

            {/* ── HEADER ────────────────────────────────────────────────────────── */}
            {resourceData ? (
                <>
                {/* ── Layer 1: Navy title bar ── */}
                <div style={{ background: HEADER_BG, boxShadow: "0 4px 20px rgba(15,40,71,0.35)", padding: "8px 24px", display: "flex", alignItems: "center", gap: "14px", flexShrink: 0 }}>
                     <button
    onClick={() => navigate(-1)}
    title="Go back"
    style={{ width:"32px", height:"32px", borderRadius:"8px", background:"rgba(255,255,255,0.12)", border:"1px solid rgba(255,255,255,0.2)", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", flexShrink:0, transition:"background 0.15s" }}
    onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.22)"}
    onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.12)"}
>
    <ArrowLeft size={16} color="white" strokeWidth={2.5} />
</button>
                    {/* Icon */}
                    <div style={{ width: "36px", height: "36px", borderRadius: "9px", background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px", flexShrink: 0 }}>🗺️</div>
                    {/* Title block */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
                            <h1 style={{ margin: 0, fontSize: "15px", fontWeight: "700", color: "#ffffff", letterSpacing: "0.01em", lineHeight: 1, whiteSpace: "nowrap" }}>Resource Activity Panel</h1>
                        </div>
                    </div>
                    {/* Live pill */}
                    <div style={{ display: "flex", alignItems: "center", gap: "6px", padding: "5px 12px", background: "rgba(16,185,129,0.15)", border: "1px solid rgba(16,185,129,0.35)", borderRadius: "20px", flexShrink: 0 }}>
                        <div style={{ width: "7px", height: "7px", borderRadius: "50%", background: "#10b981", boxShadow: "0 0 6px #10b981" }} />
                        <span style={{ fontSize: "11px", fontWeight: "700", color: "#34d399", letterSpacing: "0.04em" }}>LIVE</span>
                    </div>
                </div>

                {/* ── Layer 2: Light content bar — incident name + single row details ── */}
                <div style={{ backgroundColor: "#ffffff", borderBottom: "1px solid rgba(30,58,95,0.1)", boxShadow: "0 2px 12px rgba(30,58,95,0.06)", flexShrink: 0, padding: "10px 20px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>

                        {/* Left: incident name + resource details stacked */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                            {/* Incident name row */}
                            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
                                <span style={{ fontSize: "12px", fontWeight: "700", color: TEXT_PRIMARY, whiteSpace: "nowrap" }}>Incident Name:</span>
                                <span style={{ fontSize: "13px", fontWeight: "700", color: "#dc2626", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={incidentData.incident_name}>{incidentData.incident_name}</span>
                            </div>
                            {/* Resource / Agency / Facility — single row */}
                            <div style={{ display: "flex", alignItems: "center", gap: "0" }}>
                                {[
                                    { label: "Resource Name", value: incidentData.resource_name,          color: "#2563eb" },
                                    { label: "Agency Name",   value: incidentData.resource_agency_name,   color: "#059669" },
                                    { label: "Facility Name", value: incidentData.resource_facility_name, color: "#7c3aed" },
                                ].map((item, idx) => (
                                    <React.Fragment key={item.label}>
                                        <div style={{ minWidth: 0, flex: 1 }}>
                                            <span style={{ fontSize: "12px", fontWeight: "600", color: TEXT_SECONDARY }}>{item.label}: </span>
                                            <span style={{ fontSize: "12px", fontWeight: "700", color: item.color }} title={item.value || "N/A"}>{item.value || "N/A"}</span>
                                        </div>
                                        {idx < 2 && <div style={{ width: "1px", height: "28px", background: "rgba(30,58,95,0.1)", flexShrink: 0, margin: "0 14px" }} />}
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>

                        {/* Divider */}
                        <div style={{ width: "1px", height: "50px", background: "rgba(30,58,95,0.1)", flexShrink: 0 }} />

                        {/* Stat cards */}
                        {[
                            { label: "Total Deployed", value: deploymentStatus.active,   valueColor: "#2563eb", bgColor: "#eff6ff", borderColor: "#bfdbfe", icon: "🚀" },
                            { label: "Total Released", value: deploymentStatus.released,  valueColor: "#059669", bgColor: "#f0fdf4", borderColor: "#bbf7d0", icon: "✅" },
                            ...(hasTransportationResources ? [{
        label: "Live Tracking", value: liveVehicleData.length,
        valueColor: liveVehicleData.length > 0 ? "#059669" : "#94a3b8",
        bgColor: liveVehicleData.length > 0 ? "#f0fdf4" : "#f8fafd",
        borderColor: liveVehicleData.length > 0 ? "#bbf7d0" : INPUT_BORDER,
        icon: "📡"
    }] : []),
                        ].map(stat => (
                            <div key={stat.label} className="ap-stat-card" style={{ display: "flex", alignItems: "center", gap: "10px", padding: "7px 14px", backgroundColor: stat.bgColor, borderRadius: "10px", border: `1.5px solid ${stat.borderColor}`, flexShrink: 0, transition: "all 0.2s", cursor: "default" }}>
                                <span style={{ fontSize: "18px", lineHeight: 1 }}>{stat.icon}</span>
                                <div>
                                    <div style={{ fontSize: "20px", fontWeight: "800", color: stat.valueColor, lineHeight: 1 }}>{stat.value}</div>
                                    <div style={{ fontSize: "9.5px", fontWeight: "600", color: TEXT_MUTED, marginTop: "2px", letterSpacing: "0.03em", whiteSpace: "nowrap" }}>{stat.label}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                </>
            ) : (
                <>
                {/* ── Layer 1: Navy title bar (matches GIS Data Management) ── */}
                <div style={{
                    background: HEADER_BG,
                    boxShadow: "0 4px 20px rgba(15,40,71,0.35)",
                    padding: "8px 24px",
                    display: "flex", alignItems: "center", gap: "12px",
                    flexShrink: 0,
                }}>
                    {/* Icon + title */}
                    <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1 }}>
                        <div style={{ width: "34px", height: "34px", borderRadius: "9px", background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px", flexShrink: 0 }}>🗺️</div>
                        <div>
                            <h1 style={{ margin: 0, fontSize: "15px", fontWeight: "700", color: "#ffffff", letterSpacing: "0.01em", lineHeight: 1.2 }}>Resource Activity Panel</h1>
                        </div>
                    </div>
                    {/* Live status indicator */}
                    <div style={{ display: "flex", alignItems: "center", gap: "6px", padding: "5px 12px", background: "rgba(16,185,129,0.15)", border: "1px solid rgba(16,185,129,0.35)", borderRadius: "20px" }}>
                        <div style={{ width: "7px", height: "7px", borderRadius: "50%", background: "#10b981", boxShadow: "0 0 6px #10b981" }} />
                        <span style={{ fontSize: "11px", fontWeight: "700", color: "#34d399", letterSpacing: "0.04em" }}>LIVE</span>
                    </div>
                </div>

                {/* ── Layer 2: Light content bar — dropdown, filters, stats, facility details ── */}
                <div style={{
                    backgroundColor: "#ffffff",
                    borderBottom: "1px solid rgba(30,58,95,0.1)",
                    boxShadow: "0 2px 12px rgba(30,58,95,0.06)",
                    flexShrink: 0,
                }}>
                    {/* Controls row */}
                    <div style={{ padding: "10px 20px 10px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px" }}>

                        {/* Left: Dropdown + Filter */}
                        <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1 }}>

                            {/* Incident Dropdown */}
                            <div style={{ position: "relative", width: "380px" }}>
                                <button onClick={() => setShowIncidentDropdown(!showIncidentDropdown)}
                                    style={{ width: "100%", padding: "9px 14px", backgroundColor: "#f8fafd", border: `1.5px solid ${INPUT_BORDER}`, borderRadius: "8px", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", fontSize: "13px", fontWeight: "500", color: TEXT_PRIMARY, boxShadow: "0 1px 4px rgba(30,58,95,0.06)", transition: "border-color 0.15s" }}
                                    onMouseEnter={e => e.currentTarget.style.borderColor = ACCENT}
                                    onMouseLeave={e => e.currentTarget.style.borderColor = INPUT_BORDER}>
                                    <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", flex: 1, textAlign: "left", color: selectedIncident ? TEXT_PRIMARY : TEXT_MUTED }}>
                                        {selectedIncident === "all" ? "All Incidents" : selectedIncident ? selectedIncident.incidentName : "Select Incident"}
                                    </span>
                                    <ChevronDown size={15} style={{ transform: showIncidentDropdown ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s", flexShrink: 0, marginLeft: "8px", color: ACCENT }} />
                                </button>
                                {showIncidentDropdown && (
                                    <div style={{ position: "absolute", top: "100%", left: 0, right: 0, marginTop: "4px", backgroundColor: CARD_BG, border: CARD_BORDER, borderRadius: "10px", boxShadow: "0 8px 32px rgba(30,58,95,0.15)", maxHeight: "280px", overflowY: "auto", zIndex: 1001 }}>
                                        <div onClick={() => handleIncidentSelect("all")}
                                            style={{ padding: "11px 14px", cursor: "pointer", borderBottom: `1px solid ${DIVIDER}`, backgroundColor: selectedIncident === "all" ? ACCENT_LIGHT : CARD_BG, transition: "background-color 0.15s" }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = ACCENT_LIGHT}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = selectedIncident === "all" ? ACCENT_LIGHT : CARD_BG}>
                                            <div style={{ fontWeight: "600", color: TEXT_PRIMARY, fontSize: "13px" }}>All Incidents</div>
                                        </div>
                                        {allIncidentsData.length === 0 ? (
                                            <div style={{ padding: "14px", textAlign: "center", color: TEXT_MUTED, fontSize: "13px" }}>No incidents available</div>
                                        ) : (
                                            allIncidentsData.map((incident) => (
                                                <div key={incident.incidentId} onClick={() => handleIncidentSelect(incident)}
                                                    style={{ padding: "11px 14px", cursor: "pointer", borderBottom: `1px solid ${DIVIDER}`, backgroundColor: selectedIncident?.incidentId === incident.incidentId ? ACCENT_LIGHT : CARD_BG, transition: "background-color 0.15s" }}
                                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = ACCENT_LIGHT}
                                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = selectedIncident?.incidentId === incident.incidentId ? ACCENT_LIGHT : CARD_BG}>
                                                    <div style={{ fontWeight: "500", color: TEXT_PRIMARY, fontSize: "13px" }}>{incident.incidentName}</div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Filter Button */}
                            <div style={{ position: "relative" }}>
                                <button onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                                    style={{ padding: "9px 16px", backgroundColor: showFilterDropdown ? ACCENT : "#f8fafd", border: `1.5px solid ${showFilterDropdown ? ACCENT : INPUT_BORDER}`, borderRadius: "8px", display: "flex", alignItems: "center", gap: "7px", cursor: "pointer", fontSize: "13px", fontWeight: "600", color: showFilterDropdown ? "white" : TEXT_PRIMARY, boxShadow: "0 1px 4px rgba(30,58,95,0.06)", transition: "all 0.15s" }}>
                                    <Filter size={14} color={showFilterDropdown ? "white" : ACCENT} /><span>Filters</span>
                                </button>
                                {showFilterDropdown && (
                                    <div style={{ position: "absolute", top: "100%", left: 0, marginTop: "4px", backgroundColor: CARD_BG, border: CARD_BORDER, borderRadius: "10px", boxShadow: "0 8px 32px rgba(30,58,95,0.15)", minWidth: "240px", zIndex: 1000, padding: "8px", overflow: "hidden" }}>
                                        <div style={{ padding: "8px 12px 6px", fontSize: "10px", fontWeight: "700", color: TEXT_MUTED, textTransform: "uppercase", letterSpacing: "0.06em" }}>Toggle Map Layers</div>
                                        {[
                                            { label: "Live Vehicles", state: showVehicles, setter: setShowVehicles, icon: "🚑" },
                                            { label: "Facilities & Areas", state: showIncidents, setter: setShowIncidents, icon: "🏢" },
                                            { label: "Incident Locations", state: showIncidentMarkers, setter: setShowIncidentMarkers, icon: "🚨" },
                                            { label: "Nearby Facilities", state: showNearbyPlaces, setter: setShowNearbyPlaces, icon: "🏥" },
                                        ].map(item => (
                                            <div key={item.label} onClick={() => item.setter(!item.state)}
                                                style={{ padding: "8px 12px", cursor: "pointer", borderRadius: "7px", display: "flex", alignItems: "center", gap: "10px", transition: "background-color 0.15s", marginBottom: "2px" }}
                                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = ACCENT_LIGHT}
                                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}>
                                                <input type="checkbox" checked={item.state} onChange={() => { }} style={{ width: "15px", height: "15px", cursor: "pointer", accentColor: ACCENT }} />
                                                <span style={{ fontSize: "16px" }}>{item.icon}</span>
                                                <span style={{ fontSize: "13px", fontWeight: "500", color: TEXT_PRIMARY }}>{item.label}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Right: Stat cards — light modern style */}
                        <div style={{ display: "flex", gap: "8px" }}>
                            {[
                                { label: "Live Incidents", value: selectedIncident === "all" ? allIncidentsData.length : 1, valueColor: "#ef4444", bgColor: "#fff1f2", borderColor: "#fecdd3", icon: "🚨" },
                                { label: "Live Vehicles",  value: getTotalVehiclesCount(),                                   valueColor: "#2563eb", bgColor: "#eff6ff", borderColor: "#bfdbfe", icon: "🚑" },
                                { label: "Nearby Places",  value: nearbyPlacesData.length,                                  valueColor: "#7c3aed", bgColor: "#f5f3ff", borderColor: "#ddd6fe", icon: "📍" },
                            ].map(stat => (
                                <div key={stat.label} className="ap-stat-card" style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px 14px", backgroundColor: stat.bgColor, borderRadius: "10px", border: `1.5px solid ${stat.borderColor}`, minWidth: "130px", transition: "all 0.2s", cursor: "default" }}>
                                    <span style={{ fontSize: "20px", lineHeight: 1 }}>{stat.icon}</span>
                                    <div>
                                        <div style={{ fontSize: "20px", fontWeight: "800", color: stat.valueColor, lineHeight: 1 }}>{stat.value}</div>
                                        <div style={{ fontSize: "10px", fontWeight: "600", color: TEXT_MUTED, marginTop: "2px", letterSpacing: "0.03em", whiteSpace: "nowrap" }}>{stat.label}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* ── Resource Facility Details — redesigned, sits in the light bar ── */}
                    {!isFullscreen && facilityDetailsPanel}
                </div>
                </>
            )}

            {/* Map and Resource Panel Container */}
            <div style={{ flex: 1, padding: "14px 20px 20px", display: "flex", gap: "14px", minHeight: 0, overflow: "hidden" }}>
                {/* Map View */}
                <div ref={mapContainerRef} style={{ flex: 1, minWidth: 0, height: isFullscreen ? "100vh" : "100%", backgroundColor: CARD_BG, borderRadius: "14px", overflow: "hidden", border: CARD_BORDER, boxShadow: SHADOW, transition: "all 0.3s ease", position: "relative" }}>

                    {/* Resource Facility Details, re-shown as an overlay when the map
                        is fullscreen (it lives outside mapContainerRef normally, so the
                        Fullscreen API would otherwise hide it) — same expand/collapse
                        state (isFacilitiesExpanded) carries over since it's the same panel. */}
                    {isFullscreen && facilityDetailsPanel && (
                        <div style={{ position: "absolute", top: "10px", left: "10px", right: "60px", zIndex: 1100 }}>
                            {facilityDetailsPanel}
                        </div>
                    )}

                    {isSnappingRoutes && (
                        <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", backgroundColor: "white", padding: "24px 32px", borderRadius: "16px", boxShadow: `0 12px 40px rgba(26,63,111,0.2)`, zIndex: 1000, display: "flex", flexDirection: "column", alignItems: "center", gap: "16px", border: `2px solid ${ACCENT}`, minWidth: "300px" }}>
                            <div style={{ width: "48px", height: "48px", border: `4px solid ${ACCENT_LIGHT}`, borderTopColor: ACCENT, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                            <div style={{ textAlign: "center" }}>
                                <div style={{ fontSize: "16px", color: TEXT_PRIMARY, fontWeight: "600", marginBottom: "4px" }}>🛣️ Snapping routes to roads</div>
                                <div style={{ fontSize: "14px", color: TEXT_SECONDARY }}>Processing {Object.keys(roadSnappedRoutes).length} of {selectedIncident === "all" ? allIncidentsData.length : 1} routes</div>
                                <div style={{ fontSize: "12px", color: TEXT_MUTED, marginTop: "8px", fontStyle: "italic" }}>Please wait...</div>
                            </div>
                        </div>
                    )}

                    {loadingNearbyPlaces && (
                        <div style={{ position: "absolute", top: "12px", left: "50%", transform: "translateX(-50%)", backgroundColor: "white", padding: "8px 18px", borderRadius: "20px", boxShadow: "0 4px 16px rgba(26,63,111,0.15)", zIndex: 999, display: "flex", alignItems: "center", gap: "8px", fontSize: "12px", color: TEXT_SECONDARY, border: CARD_BORDER }}>
                            <div style={{ width: "14px", height: "14px", border: `2px solid ${ACCENT_LIGHT}`, borderTopColor: ACCENT, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                            Loading nearby facilities...
                        </div>
                    )}

                    <GoogleMap
                        mapContainerStyle={mapContainerStyle}
                        center={mapViewRef.current.center}
                        zoom={mapViewRef.current.zoom}
                        onLoad={onMapLoad}
                        onIdle={onMapIdle}
                        onDragStart={onMapDragStart}
                        onZoomChanged={onMapZoomChanged}
                        mapTypeId={mapTypeMap[activeBaseLayer] || 'terrain'}
                        options={{ minZoom: 4, zoomControl: true, mapTypeControl: false, streetViewControl: false, fullscreenControl: false, gestureHandling: 'greedy', disableDefaultUI: true }}
                    >
                        {stateGeoJSON && geoJsonToPaths(stateGeoJSON).map((paths, idx) => (
                            <Polygon key={`state-boundary-${idx}`} paths={paths} options={{ strokeColor: "#b11119", strokeWeight: 2, fillColor: "#b11119", fillOpacity: 0.08 }} />
                        ))}

                        {(() => {
                            const { elements, polygonElements, polylineElements } = renderMapElements();
                            return (<>{polygonElements}{/* {polylineElements} */}{elements}</>);
                        })()}

                        {activeFacilityRouteId && facilityRoutes[activeFacilityRouteId] && (() => {
                            const route = facilityRoutes[activeFacilityRouteId];
                            return (
                                <>
                                    <GPolyline path={route.path} options={{ strokeColor: "#7c3aed", strokeWeight: 8, strokeOpacity: 0.18, zIndex: 5 }} />
                                    <GPolyline path={route.path} options={{ strokeColor: route.isFallback ? "#f59e0b" : "#7c3aed", strokeWeight: 4, strokeOpacity: 0.85, strokeDashArray: route.isFallback ? "8 6" : undefined, zIndex: 6, icons: route.isFallback ? undefined : [{ icon: { path: window.google?.maps?.SymbolPath?.FORWARD_CLOSED_ARROW, scale: 3, strokeColor: '#7c3aed', fillColor: '#7c3aed', fillOpacity: 1 }, offset: '50%', repeat: '120px' }] }} onClick={() => setActiveFacilityRouteId(null)} />
                                    {route.distanceKm && (() => {
                                        const mid = route.path[Math.floor(route.path.length / 2)];
                                        return (<OverlayView position={mid} mapPaneName={OverlayView.FLOAT_PANE} getPixelPositionOffset={(w, h) => ({ x: -w / 2, y: -h - 8 })}><div style={{ backgroundColor: '#7c3aed', color: 'white', padding: '5px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '700', boxShadow: '0 3px 10px rgba(124,58,237,0.45)', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', border: '2px solid white' }} onClick={() => setActiveFacilityRouteId(null)}>🛣️ {route.distanceKm} km &nbsp;·&nbsp; ⏱ {route.durationMin} min &nbsp;<span style={{ fontSize: '10px', opacity: 0.8 }}>✕</span></div></OverlayView>);
                                    })()}
                                </>
                            );
                        })()}

                        {activeInfoWindow && (<InfoWindow position={activeInfoWindow.position} onCloseClick={() => setActiveInfoWindow(null)}><div>{activeInfoWindow.content}</div></InfoWindow>)}

                        {isFilterLoading && (
                            <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", backgroundColor: "white", padding: "20px 28px", borderRadius: "12px", boxShadow: "0 8px 24px rgba(0,0,0,0.15)", zIndex: 1000, display: "flex", alignItems: "center", gap: "14px", border: "1px solid #e5e7eb", minWidth: "260px" }}>
                                <div style={{ width: "22px", height: "22px", flexShrink: 0, border: "3px solid #e0e7ff", borderTopColor: "#3b82f6", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
                                <div><div style={{ fontSize: "14px", fontWeight: "600", color: "#1f2937" }}>Applying filters…</div><div style={{ fontSize: "12px", color: "#6b7280", marginTop: "2px" }}>Updating map layers</div></div>
                            </div>
                        )}

                        {/* Layer Control */}
                        <div style={{ position: 'absolute', top: '12px', right: '12px', zIndex: 1000 }}>
                            <button onClick={() => setIsLayerMenuOpen(!isLayerMenuOpen)} title="Map Layers"
                                style={{ width: '46px', height: '46px', backgroundColor: CARD_BG, border: `2px solid #1a3f6f`, borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: SHADOW }}>
                                <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="#1a3f6f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /><path d="M2 12L12 17L22 12" stroke="#1a3f6f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /><path d="M2 17L12 22L22 17" stroke="#1a3f6f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            </button>
                            {isLayerMenuOpen && (
                                <div style={{ position: 'absolute', top: '54px', right: '0', backgroundColor: CARD_BG, borderRadius: '10px', boxShadow: "0 8px 32px rgba(30,58,95,0.15)", minWidth: '180px', overflow: 'hidden', border: CARD_BORDER }}>
                                    {[{ label: 'Road View', key: 'road' }, { label: 'Satellite View', key: 'satellite' }, { label: 'Terrain View', key: 'terrain' }, { label: 'Hybrid View', key: 'hybrid' }].map(layer => (
                                        <div key={layer.key} onClick={() => { setActiveBaseLayer(layer.key); setIsLayerMenuOpen(false); }}
                                            style={{ padding: '11px 16px', cursor: 'pointer', backgroundColor: activeBaseLayer === layer.key ? ACCENT_LIGHT : CARD_BG, fontWeight: activeBaseLayer === layer.key ? '700' : '400', color: activeBaseLayer === layer.key ? ACCENT : TEXT_PRIMARY, fontSize: '13px', transition: "background-color 0.15s" }}
                                            onMouseEnter={(e) => { if (activeBaseLayer !== layer.key) e.currentTarget.style.backgroundColor = ACCENT_LIGHT; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = activeBaseLayer === layer.key ? ACCENT_LIGHT : CARD_BG; }}>
                                            {layer.label}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Fullscreen Button (below Layer Control) */}
                        <button
                            onClick={handleFullscreen}
                            title={isFullscreen ? "Exit Full Screen" : "Full Screen"}
                            style={{
                                position: 'absolute', top: '68px', right: '12px', zIndex: 1000,
                                width: 46, height: 46, borderRadius: 10,
                                border: isFullscreen ? '2px solid #3b82f6' : '2px solid #1a3f6f',
                                background: isFullscreen ? 'linear-gradient(135deg,#eff6ff,#dbeafe)' : CARD_BG,
                                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                boxShadow: isFullscreen ? '0 2px 12px rgba(59,130,246,0.35)' : SHADOW,
                                transition: 'all 0.2s ease',
                            }}
                            onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.08)')}
                            onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
                        >
                            {isFullscreen ? (
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
                                </svg>
                            ) : (
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1a3f6f" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
                                </svg>
                            )}
                        </button>

                        <ActivePanelMapLegend
                            mapRef={mapRef}
                            topOffset={isFullscreen && facilityDetailsPanel ? 70 : 10}
                            isInfoWindowOpen={activeInfoWindow !== null}
                            selectedIncident={selectedIncident}
                            allIncidentsData={allIncidentsData}
                            liveVehicleData={liveVehicleData}
                            resourceData={resourceData}
                            visibleResources={visibleResources}
                            nearbyPlacesData={nearbyPlacesData}
                            hospitalData={hospitalData}
                            showVehicles={showVehicles}
                            showIncidents={showIncidents}
                            showIncidentMarkers={showIncidentMarkers}
                            showNearbyPlaces={showNearbyPlaces}
                            showAllLayers={showAllLayers}
                            setShowAllLayers={setShowAllLayers}
                            selectedLegendFilters={selectedLegendFilters}
                            setSelectedLegendFilters={handleLegendFilterChange}
                        />
                    </GoogleMap>
                </div>

                {/* Resource Info Panel */}
                {showResourcePanel && resourceData && (
                    <div style={{ width: "380px", height: "100%", backgroundColor: CARD_BG, borderRadius: "12px", border: CARD_BORDER, boxShadow: SHADOW, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                        <div style={{ padding: "20px", borderBottom: `1px solid ${DIVIDER}`, display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: "#f9fafb" }}>
                            <div><h3 style={{ fontSize: "18px", fontWeight: "600", color: TEXT_PRIMARY, marginBottom: "4px" }}>Deployment Details</h3><p style={{ fontSize: "13px", color: TEXT_SECONDARY }}>{resourceData.resource_name}</p></div>
                            <button onClick={() => setShowResourcePanel(false)} style={{ padding: "8px", borderRadius: "6px", border: "none", backgroundColor: "transparent", cursor: "pointer", color: TEXT_SECONDARY }}><X size={20} /></button>
                        </div>
                        <div style={{ padding: "16px", borderBottom: `1px solid ${DIVIDER}` }}>
                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px" }}>
                                <div style={{ textAlign: "center", flex: 1 }}><div style={{ fontSize: "24px", fontWeight: "600", color: "#059669" }}>{resourceData.available_quantity}</div><div style={{ fontSize: "12px", color: TEXT_SECONDARY }}>Available</div></div>
                                <div style={{ textAlign: "center", flex: 1, borderLeft: `1px solid ${DIVIDER}`, borderRight: `1px solid ${DIVIDER}` }}><div style={{ fontSize: "24px", fontWeight: "600", color: ACCENT }}>{deploymentStatus.active}</div><div style={{ fontSize: "12px", color: TEXT_SECONDARY }}>Active</div></div>
                                <div style={{ textAlign: "center", flex: 1 }}><div style={{ fontSize: "24px", fontWeight: "600", color: TEXT_MUTED }}>{deploymentStatus.released}</div><div style={{ fontSize: "12px", color: TEXT_SECONDARY }}>Released</div></div>
                            </div>
                            <div style={{ marginTop: "12px" }}>
                                <p style={{ fontSize: "14px", color: TEXT_SECONDARY, marginBottom: "8px" }}><strong>Facility:</strong> {resourceData.resource_facility_name}</p>
                                <p style={{ fontSize: "14px", color: TEXT_SECONDARY, marginBottom: "8px" }}><strong>Agency:</strong> {resourceData.resource_agency_name}</p>
                                <p style={{ fontSize: "14px", color: TEXT_SECONDARY }}><strong>Region:</strong> {resourceData.resource_region_name}</p>
                            </div>
                        </div>
                        <div style={{ flex: 1, overflowY: "auto", padding: "16px" }}>
                            <h4 style={{ fontSize: "16px", fontWeight: "600", color: TEXT_PRIMARY, marginBottom: "12px" }}>Deployment History</h4>
                            {(!resourceData.deployedResourceList || resourceData.deployedResourceList.length === 0) ? (
                                <div style={{ textAlign: "center", padding: "40px 20px", color: TEXT_MUTED }}><p>No deployments found</p></div>
                            ) : (
                                resourceData.deployedResourceList.map((deployment, index) => (
                                    <div key={deployment.id || index} style={{ padding: "14px", marginBottom: "12px", borderRadius: "8px", border: CARD_BORDER, backgroundColor: deployment.is_deployed ? "#f0fdf4" : "#f9fafb" }}>
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
                                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                                {deployment.vehicle_type === "AMBULANCE" ? <Ambulance size={16} className="text-red-500" /> : <Car size={16} className="text-blue-500" />}
                                                <h4 style={{ fontSize: "14px", fontWeight: "600", color: TEXT_PRIMARY, margin: 0 }}>{deployment.vehicle_number || `Deployment ${index + 1}`}</h4>
                                            </div>
                                            <span style={{ padding: "4px 8px", borderRadius: "4px", fontSize: "12px", fontWeight: "600", backgroundColor: deployment.is_deployed ? "#d1fae5" : "#f3f4f6", color: deployment.is_deployed ? "#065f46" : TEXT_MUTED }}>{deployment.is_deployed ? "ACTIVE" : "RELEASED"}</span>
                                        </div>
                                        <div style={{ fontSize: "13px", color: TEXT_SECONDARY, marginBottom: "4px" }}><strong>Quantity:</strong> {deployment.deploy_quantity}</div>
                                        {deployment.deployed_date_and_time && <div style={{ fontSize: "12px", color: TEXT_MUTED, marginBottom: "4px" }}>Deployed: {formatDateTime(deployment.deployed_date_and_time)}</div>}
                                        {deployment.released_date_and_time && <div style={{ fontSize: "12px", color: TEXT_MUTED }}>Released: {formatDateTime(deployment.released_date_and_time)}</div>}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ActivePanelView;